import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Folder as FolderIcon,
  ArrowBack as ArrowBackIcon,
  Description as FileIcon,
  VideoLibrary as VideoIcon,
  Assignment as ExerciseIcon,
  Flag as ObjectiveIcon,
  Quiz as QuizIcon,
  History as HistoryIcon,
  Chat as ChatIcon,
  Lightbulb as SuggestionIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  RateReview as EvaluateIcon,
  AccountTree as SetupIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  useDraggable,
  useDroppable,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { toast } from 'react-toastify';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import invoiceService from '../services/invoice.service';
import courseService from '../services/course.service';
import patternService from '../services/pattern.service';
import exerciseService from '../services/exercise.service';
import sessionService, { Session, SessionItem } from '../services/session.service';
import videoService from '../services/video.service';
import { Video } from '../types';
import userService from '../services/user.service';
import courseContainerService from '../services/courseContainerService';
import patternContainerService from '../services/patternContainerService';
import exerciseContainerService, { ExerciseContainer } from '../services/exerciseContainerService';
import sessionContainerService, { SessionContainer } from '../services/sessionContainerService';
import videoContainerService from '../services/videoContainer.service';
import activeSessionService from '../services/activeSession.service';
import seatAssignmentService, { SeatAssignment } from '../services/seatAssignment.service';
import exerciseEvaluationService from '../services/exerciseEvaluation.service';
import studentActionService from '../services/studentAction.service';
import { WebSocketService } from '../services/websocket';
import { Course, Exercise, User, CourseContainer, CompositePattern } from '../types';
import compositePatternService from '../services/compositePattern.service';
import PdfDisplay from '../components/PdfDisplay';
import SuggestionsColumn from '../components/inprogress/SuggestionsColumn';
import PatternInstanceModal from '../components/PatternInstanceModal';
import CompositePatternInstanceModal from '../components/CompositePatternInstanceModal';
import ExerciseEvaluationModal, { ExerciseEvaluation } from '../components/ExerciseEvaluationModal';
import SetupNavigationModal from '../components/SetupNavigationModal';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  color: string;
}

interface ActiveStudent extends Student {
  order: number;
  arrival_time: Dayjs;
  seat_number?: number;
  seat_assignment_id?: number;
}

interface VideoContainer {
  id: number;
  title: string;
  parent: number | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  order: number;
  children_count: number;
  videos_count: number;
}

// Extended SessionItem with thumbnail URLs
interface ExtendedSessionItem extends SessionItem {
  course_thumbnail_url?: string;
  exercise_pattern_thumbnail_url?: string;
  // Quick recap references
  quick_recap_course?: number | null;
  quick_recap_course_title?: string | null;
  quick_recap_course_pdf_url?: string | null;
  quick_recap_video?: number | null;
  quick_recap_video_title?: string | null;
  quick_recap_video_url?: string | null;
}

// Recently integrated item
interface RecentlyIntegratedItem {
  id: string;
  type: 'course' | 'exercise' | 'video';
  itemId: number;
  title: string;
  thumbnail_url?: string;
  addedAt: Date;
}

// Sortable Timeline Item Component
interface SortableTimelineItemProps {
  item: ExtendedSessionItem;
  index: number;
  onRemove: (itemId: number) => void;
  onOpenQuickRecap?: (item: ExtendedSessionItem) => void;
  onEvaluateExercise?: (item: ExtendedSessionItem) => void;
  isOver?: boolean;
  isSelected?: boolean;
  onItemMouseDown?: (index: number, e: React.MouseEvent) => void;
  onItemMouseEnter?: (index: number) => void;
}

const SortableTimelineItem: React.FC<SortableTimelineItemProps> = ({ item, index, onRemove, onOpenQuickRecap, onEvaluateExercise, isOver, isSelected, onItemMouseDown, onItemMouseEnter }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `timeline-item-${item.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasQuickRecap = item.quick_recap_course || item.quick_recap_video;

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        borderRadius: 1,
        bgcolor: isOver ? 'primary.light' : isSelected ? 'primary.light' : 'background.paper',
        border: '2px solid',
        borderColor: isOver ? 'primary.main' : isSelected ? 'primary.main' : 'divider',
        display: 'flex',
        alignItems: 'center',
        userSelect: 'none',
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          mr: 1,
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIcon sx={{ fontSize: 20, color: 'action.disabled' }} />
      </Box>
      <Box
        onMouseDown={(e) => {
          e.stopPropagation();
          onItemMouseDown?.(index, e);
        }}
        onMouseEnter={() => onItemMouseEnter?.(index)}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
      <Typography variant="caption" color="text.secondary" sx={{ mr: 1, minWidth: 20 }}>
        {index + 1}.
      </Typography>
      <Avatar
        src={
          item.item_type === 'course' ? item.course_thumbnail_url :
          item.item_type === 'exercise' ? item.exercise_pattern_thumbnail_url :
          item.item_type === 'video' ? item.video_thumbnail_url :
          undefined
        }
        variant="rounded"
        sx={{ width: 32, height: 32, mr: 1 }}
      >
        {item.item_type === 'course' ? <FileIcon fontSize="small" /> :
         item.item_type === 'exercise' ? <ExerciseIcon fontSize="small" /> :
         <VideoIcon fontSize="small" />}
      </Avatar>
      <ListItemText
        primary={
          item.item_type === 'course' ? item.course_title :
          item.item_type === 'exercise' ? item.exercise_pattern :
          item.item_type === 'video' ? item.video_title :
          'Unknown'
        }
        secondary={
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={item.item_type === 'course' ? 'Cours' :
                     item.item_type === 'exercise' ? 'Exercice' :
                     'Vidéo'}
              size="small"
            />
            {hasQuickRecap && (
              <Chip
                label="Quick Recap"
                size="small"
                color="info"
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Box>
        }
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ component: 'div' }}
      />
      </Box>
      {hasQuickRecap && onOpenQuickRecap && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onOpenQuickRecap(item);
          }}
          sx={{ mr: 1 }}
          title="Voir le Quick Recap"
        >
          <SuggestionIcon fontSize="small" color="info" />
        </IconButton>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
        {item.item_type === 'exercise' && onEvaluateExercise && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onEvaluateExercise(item);
            }}
            sx={{
              width: 40,
              height: 40,
              minWidth: 40,
              minHeight: 40,
              maxWidth: 40,
              maxHeight: 40,
              p: 0,
              m: 0,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
            title="Évaluer l'exercice"
          >
            <EvaluateIcon sx={{ fontSize: 20, display: 'block' }} />
          </IconButton>
        )}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.id);
          }}
          sx={{
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            maxWidth: 40,
            maxHeight: 40,
            p: 0,
            m: 0,
            borderRadius: '50%',
            bgcolor: 'error.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              bgcolor: 'error.dark',
            },
          }}
          title="Supprimer"
        >
          <CloseIcon sx={{ fontSize: 20, display: 'block' }} />
        </IconButton>
      </Box>
    </ListItem>
  );
};

// Draggable Navigation Item Component
interface DraggableNavItemProps {
  id: string;
  children: React.ReactNode;
}

const DraggableNavItem: React.FC<DraggableNavItemProps> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

// Droppable Timeline Area Component
interface DroppableTimelineAreaProps {
  children: React.ReactNode;
  isOver?: boolean;
  onClickBackground?: () => void;
}

const DroppableTimelineArea: React.FC<DroppableTimelineAreaProps> = ({ children, isOver, onClickBackground }) => {
  const { setNodeRef } = useDroppable({
    id: 'timeline-drop-area',
  });

  const handleClick = (e: React.MouseEvent) => {
    // Only deselect if clicking directly on the background (not on children)
    if (e.target === e.currentTarget && onClickBackground) {
      onClickBackground();
    }
  };

  return (
    <Box
      ref={setNodeRef}
      onClick={handleClick}
      sx={{
        flex: 1,
        overflow: 'auto',
        p: 2,
        minHeight: 0,
        bgcolor: isOver ? 'action.hover' : 'transparent',
        transition: 'background-color 0.2s',
      }}
    >
      {children}
    </Box>
  );
};

const InProgress: React.FC = () => {
  const { user } = useAuthStore();
  const [activeStudents, setActiveStudents] = useState<ActiveStudent[]>([]);
  const [studentSessionMap, setStudentSessionMap] = useState<Map<number, number>>(new Map()); // student_id -> session_id
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [suggestedStudents, setSuggestedStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<ActiveStudent | null>(null);
  const [rightTabValue, setRightTabValue] = useState(3); // 3 = Séances
  const [searchValue, setSearchValue] = useState<Student | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [draggedStudent, setDraggedStudent] = useState<Student | null>(null);


  // Active drag state for @dnd-kit
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [activeDragItem, setActiveDragItem] = useState<{
    type: 'course' | 'exercise' | 'video' | 'session' | 'timeline';
    data: any;
  } | null>(null);

  // End session modal state
  const [endSessionDialogOpen, setEndSessionDialogOpen] = useState(false);
  const [studentToEnd, setStudentToEnd] = useState<ActiveStudent | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [sessionPrice, setSessionPrice] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountLabel, setDiscountLabel] = useState('');
  const [startTimePickerOpen, setStartTimePickerOpen] = useState(false);
  const [endTimePickerOpen, setEndTimePickerOpen] = useState(false);

  // Navigation content state
  const [courses, setCourses] = useState<Course[]>([]);
  const [patterns, setPatterns] = useState<any[]>([]);
  const [compositePatterns, setCompositePatterns] = useState<CompositePattern[]>([]);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [teachers, setTeachers] = useState<User[]>([]);

  // Container state for each type
  const [courseContainers, setCourseContainers] = useState<CourseContainer[]>([]);
  const [patternContainers, setPatternContainers] = useState<any[]>([]);
  const [exerciseContainers, setExerciseContainers] = useState<ExerciseContainer[]>([]);
  const [sessionContainers, setSessionContainers] = useState<SessionContainer[]>([]);
  const [videoContainers, setVideoContainers] = useState<VideoContainer[]>([]);

  // Navigation state for each type
  const [currentCourseContainer, setCurrentCourseContainer] = useState<CourseContainer | null>(null);
  const [courseNavigationPath, setCourseNavigationPath] = useState<CourseContainer[]>([]);

  const [currentPatternContainer, setCurrentPatternContainer] = useState<any | null>(null);
  const [patternNavigationPath, setPatternNavigationPath] = useState<any[]>([]);

  const [currentExerciseContainer, setCurrentExerciseContainer] = useState<ExerciseContainer | null>(null);
  const [exerciseNavigationPath, setExerciseNavigationPath] = useState<ExerciseContainer[]>([]);

  const [currentSessionContainer, setCurrentSessionContainer] = useState<SessionContainer | null>(null);
  const [sessionNavigationPath, setSessionNavigationPath] = useState<SessionContainer[]>([]);

  const [currentVideoContainer, setCurrentVideoContainer] = useState<VideoContainer | null>(null);
  const [videoNavigationPath, setVideoNavigationPath] = useState<VideoContainer[]>([]);

  // Expanded session state (for viewing session items)
  const [expandedSession, setExpandedSession] = useState<Session | null>(null);

  // Expanded teacher state (for viewing teacher content)
  const [expandedTeacher, setExpandedTeacher] = useState<User | null>(null);
  const [teacherCourses, setTeacherCourses] = useState<Course[]>([]);
  const [teacherExercises, setTeacherExercises] = useState<Exercise[]>([]);
  const [teacherVideos, setTeacherVideos] = useState<Video[]>([]);
  const [teacherSessions, setTeacherSessions] = useState<Session[]>([]);

  // Session timeline items (dropped items)
  const [timelineItems, setTimelineItems] = useState<ExtendedSessionItem[]>([]);

  // Recently integrated items
  const [recentlyIntegrated, setRecentlyIntegrated] = useState<RecentlyIntegratedItem[]>([]);

  // Pattern instance modal state
  const [patternInstanceModalOpen, setPatternInstanceModalOpen] = useState(false);
  const [selectedPatternForInstances, setSelectedPatternForInstances] = useState<any | null>(null);
  const [pendingPatternDropIndex, setPendingPatternDropIndex] = useState<number | null>(null);

  // Composite pattern modal
  const [compositeModalOpen, setCompositeModalOpen] = useState(false);
  const [selectedCompositePattern, setSelectedCompositePattern] = useState<CompositePattern | null>(null);

  // Exercise evaluation modal
  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
  const [exerciseToEvaluate, setExerciseToEvaluate] = useState<ExtendedSessionItem | null>(null);
  const [exercisePatternId, setExercisePatternId] = useState<number | undefined>(undefined);

  // Student actions modals
  const [objectivesDialogOpen, setObjectivesDialogOpen] = useState(false);
  const [quizzesDialogOpen, setQuizzesDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [exchangesDialogOpen, setExchangesDialogOpen] = useState(false);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [studentActions, setStudentActions] = useState<any[]>([]);
  const [loadingActions, setLoadingActions] = useState(false);

  // Seat configuration
  const DEFAULT_SEATS = [1, 2, 3, 4, 5, 6];
  const [availableSeats, setAvailableSeats] = useState<number[]>(() => {
    try {
      const stored = localStorage.getItem('teacher_available_seats');
      if (stored) return JSON.parse(stored);
    } catch {}
    return DEFAULT_SEATS;
  });
  const [seatConfigOpen, setSeatConfigOpen] = useState(false);
  const [newSeatInput, setNewSeatInput] = useState('');
  const [quickRecapDialogOpen, setQuickRecapDialogOpen] = useState(false);
  const [quickRecapItem, setQuickRecapItem] = useState<ExtendedSessionItem | null>(null);

  // Partial preview state
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [partialPreviewOpen, setPartialPreviewOpen] = useState(false);
  const [partialPdfUrl, setPartialPdfUrl] = useState<string | null>(null);
  const partialPdfIframeRef = useRef<HTMLIFrameElement>(null);
  const [generatingPartialPdf, setGeneratingPartialPdf] = useState(false);

  // Click and drag selection state
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const [dragStartIndex, setDragStartIndex] = useState<number | null>(null);

  // Drag and drop sensors for timeline reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    })
  );

  // Helper function to add item to recently integrated list
  const addToRecentlyIntegrated = (type: 'course' | 'exercise' | 'video', itemId: number, title: string, thumbnail_url?: string) => {
    const newItem: RecentlyIntegratedItem = {
      id: `${type}-${itemId}-${Date.now()}`,
      type,
      itemId,
      title,
      thumbnail_url,
      addedAt: new Date(),
    };

    setRecentlyIntegrated(prev => {
      // Add new item at the beginning and keep only the last 20 items
      const updated = [newItem, ...prev];
      return updated.slice(0, 20);
    });
  };

  /** Présences actives + places → liste « Élèves en cours » ; rechargée sur chaque notification « presence ». */
  const reloadActiveStudents = useCallback(async (students: Student[]) => {
    const [activeSessions, seatAssignments] = await Promise.all([
      activeSessionService.getActiveForTeacher(),
      seatAssignmentService.getForTeacher(),
    ]);
    const seatMap = new Map<number, SeatAssignment>();
    seatAssignments.forEach((a) => seatMap.set(a.student, a));
    const teacherTimezone = user?.timezone || 'Pacific/Noumea';
    const loaded: ActiveStudent[] = [];
    const sessionMap = new Map<number, number>();
    (activeSessions || []).forEach((session) => {
      const student = session.student_data || students.find((s) => s.id === session.student);
      if (!student) return;
      const seat = seatMap.get(student.id);
      loaded.push({
        ...student,
        order: loaded.length,
        arrival_time: dayjs.tz(session.arrival_time, teacherTimezone),
        seat_number: seat?.seat_number,
        seat_assignment_id: seat?.id,
      });
      if (session.id) sessionMap.set(student.id, session.id);
    });
    // fusion : un élève déjà affiché garde son heure d'arrivée (arrondie à la main, elle sert à la facture)
    setActiveStudents((prev) => loaded.map((s) => {
      const before = prev.find((p) => p.id === s.id);
      return before ? { ...s, arrival_time: before.arrival_time, order: before.order } : s;
    }));
    setStudentSessionMap(sessionMap);
    // l'élève sélectionné suit sa fiche à jour (place) ; parti → désélectionné
    setSelectedStudent((prev) => {
      if (!prev) return prev;
      const fresh = loaded.find((s) => s.id === prev.id);
      return fresh ? { ...fresh, arrival_time: prev.arrival_time, order: prev.order } : null;
    });
    return loaded;
  }, [user]);
  const reloadRef = useRef(reloadActiveStudents);
  reloadRef.current = reloadActiveStudents;
  const studentsRef = useRef<Student[]>(allStudents);
  studentsRef.current = allStudents;

  useEffect(() => {
    // Fetch all students and active sessions
    const fetchData = async () => {
      try {
        // Fetch students
        const response = await api.get('/students/');
        let students: Student[] = [];
        if (response.data && response.data.results) {
          students = Array.isArray(response.data.results) ? response.data.results : [];
        } else {
          students = Array.isArray(response.data) ? response.data : [];
        }
        setAllStudents(students);

        // Fetch active sessions for current teacher
        try {
          const loadedActiveStudents = await reloadActiveStudents(students);
          if (loadedActiveStudents.length > 0) {
            setSelectedStudent(loadedActiveStudents[0]);

            // Build suggestions from today's time slots (recurring + punctual)
            const activeStudentIds = new Set(loadedActiveStudents.map(s => s.id));
            try {
              const ncNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Pacific/Noumea' }));
              const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
              const todayName = dayNames[ncNow.getDay()];
              const todayDate = `${ncNow.getFullYear()}-${String(ncNow.getMonth()+1).padStart(2,'0')}-${String(ncNow.getDate()).padStart(2,'0')}`;

              const [tsResp, ptsResp] = await Promise.all([
                api.get('/time-slots/'),
                api.get('/punctual-time-slots/', { params: { date: todayDate } }),
              ]);
              const timeSlots = Array.isArray(tsResp.data) ? tsResp.data : tsResp.data?.results || [];
              const punctualSlots = Array.isArray(ptsResp.data) ? ptsResp.data : ptsResp.data?.results || [];

              // Students with recurring slots today
              const todayStudentIds = new Set<number>();
              timeSlots.filter((ts: any) => ts.day === todayName).forEach((ts: any) => todayStudentIds.add(ts.student));
              // Students with punctual slots today
              punctualSlots.forEach((pts: any) => todayStudentIds.add(pts.student));

              // Filter: today's students, not already active
              const todaySuggestions = students.filter(s => todayStudentIds.has(s.id) && !activeStudentIds.has(s.id));
              // Sort by start time
              const getEarliestTime = (sid: number) => {
                const times: string[] = [];
                timeSlots.filter((ts: any) => ts.student === sid && ts.day === todayName).forEach((ts: any) => times.push(ts.start_time));
                punctualSlots.filter((pts: any) => pts.student === sid).forEach((pts: any) => times.push(pts.start_time));
                return times.sort()[0] || '99:99';
              };
              todaySuggestions.sort((a, b) => getEarliestTime(a.id).localeCompare(getEarliestTime(b.id)));

              if (todaySuggestions.length > 0) {
                setSuggestedStudents(todaySuggestions);
              } else {
                // Fallback: random if no one scheduled today
                const remaining = students.filter(s => !activeStudentIds.has(s.id));
                setSuggestedStudents([...remaining].sort(() => 0.5 - Math.random()).slice(0, 5));
              }
            } catch {
              const remaining = students.filter(s => !activeStudentIds.has(s.id));
              setSuggestedStudents([...remaining].sort(() => 0.5 - Math.random()).slice(0, 5));
            }
          } else {
            // No active sessions - suggest today's students
            try {
              const ncNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Pacific/Noumea' }));
              const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
              const todayName = dayNames[ncNow.getDay()];
              const todayDate = `${ncNow.getFullYear()}-${String(ncNow.getMonth()+1).padStart(2,'0')}-${String(ncNow.getDate()).padStart(2,'0')}`;

              const [tsResp, ptsResp] = await Promise.all([
                api.get('/time-slots/'),
                api.get('/punctual-time-slots/', { params: { date: todayDate } }),
              ]);
              const timeSlots = Array.isArray(tsResp.data) ? tsResp.data : tsResp.data?.results || [];
              const punctualSlots = Array.isArray(ptsResp.data) ? ptsResp.data : ptsResp.data?.results || [];

              const todayStudentIds = new Set<number>();
              timeSlots.filter((ts: any) => ts.day === todayName).forEach((ts: any) => todayStudentIds.add(ts.student));
              punctualSlots.forEach((pts: any) => todayStudentIds.add(pts.student));

              const todaySuggestions = students.filter(s => todayStudentIds.has(s.id));
              const getEarliestTime = (sid: number) => {
                const times: string[] = [];
                timeSlots.filter((ts: any) => ts.student === sid && ts.day === todayName).forEach((ts: any) => times.push(ts.start_time));
                punctualSlots.filter((pts: any) => pts.student === sid).forEach((pts: any) => times.push(pts.start_time));
                return times.sort()[0] || '99:99';
              };
              todaySuggestions.sort((a, b) => getEarliestTime(a.id).localeCompare(getEarliestTime(b.id)));

              if (todaySuggestions.length > 0) {
                setSuggestedStudents(todaySuggestions);
              } else {
                setSuggestedStudents([...students].sort(() => 0.5 - Math.random()).slice(0, 5));
              }
            } catch {
              setSuggestedStudents([...students].sort(() => 0.5 - Math.random()).slice(0, 5));
            }
          }
        } catch (sessionError) {
          console.error('Error fetching active sessions:', sessionError);
          setSuggestedStudents([...students].sort(() => 0.5 - Math.random()).slice(0, 5));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setAllStudents([]);
        setSuggestedStudents([]);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Connect to WebSocket for real-time student notifications (questions, exercise done)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const ws = new WebSocketService('/ws/notifications/', () => localStorage.getItem('token'));
    ws.connect();

    ws.on('notification', (msg) => {   // le consumer envoie type 'notification' (notification_message = nom interne channels)
      const category = msg.category as string;
      const title = msg.title as string;
      const body = msg.body as string;

      if (category === 'student_question') {
        toast.info(`${title}: "${body}"`, { autoClose: 10000 });
      } else if (category === 'exercise_done') {
        toast.success(`${title}: ${body}`, { autoClose: 5000 });
      } else if (category === 'presence') {
        const data = (msg.data || {}) as { event?: string };
        if (data.event === 'arrived') toast.success(`${title} · ${body}`);
        else if (data.event === 'left') toast.info(`${title} · ${body}`);
        reloadRef.current(studentsRef.current).catch(() => undefined);
      }
    });

    return () => {
      ws.disconnect();
    };
  }, []);

  // Fetch navigation content based on selected tab and current container
  useEffect(() => {
    const fetchContent = async () => {
      try {
        switch (rightTabValue) {
          case 0: // Cours
            if (currentCourseContainer) {
              // Load contents of current container
              const contents = await courseContainerService.getContainerContents(currentCourseContainer.id);
              setCourseContainers(Array.isArray(contents?.containers) ? contents.containers : []);
              setCourses(Array.isArray(contents?.courses) ? contents.courses : []);
            } else {
              // Load root level items
              const [containerData, courseData] = await Promise.all([
                courseContainerService.getContainers(null),
                courseService.getCourses()
              ]);
              const validContainerData = Array.isArray(containerData) ? containerData : [];
              const validCourseData = Array.isArray(courseData) ? courseData : [];
              const rootCourses = validCourseData.filter(c => !c.container);
              setCourseContainers(validContainerData);
              setCourses(rootCourses);
            }
            break;

          case 1: // Patterns
            if (currentPatternContainer) {
              // Load contents of current container + composites
              const [contents, composites] = await Promise.all([
                patternContainerService.getContainerContents(currentPatternContainer.id),
                compositePatternService.getAll({ container: currentPatternContainer.id }),
              ]);
              setPatternContainers(Array.isArray(contents?.containers) ? contents.containers : []);
              setPatterns(Array.isArray(contents?.patterns) ? contents.patterns : []);
              setCompositePatterns(composites);
            } else {
              // Load root level items
              const [containerData, patternData, composites] = await Promise.all([
                patternContainerService.getContainers(null),
                patternService.getPatterns(null),
                compositePatternService.getAll({ container: null }),
              ]);
              setPatternContainers(Array.isArray(containerData) ? containerData : []);
              setPatterns(Array.isArray(patternData) ? patternData : []);
              setCompositePatterns(composites);
            }
            break;

          case 2: // Exercices
            if (currentExerciseContainer) {
              // Load contents of current container
              const contents = await exerciseContainerService.getContainerContents(currentExerciseContainer.id);
              setExerciseContainers(Array.isArray(contents?.containers) ? contents.containers : []);
              setExercises(Array.isArray(contents?.exercises) ? contents.exercises : []);
            } else {
              // Load root level items
              const contents = await exerciseContainerService.getRootContents();
              setExerciseContainers(Array.isArray(contents?.containers) ? contents.containers : []);
              setExercises(Array.isArray(contents?.exercises) ? contents.exercises : []);
            }
            break;

          case 3: // Séances
            if (currentSessionContainer) {
              // Load contents of current container
              const contents = await sessionContainerService.getContainerContents(currentSessionContainer.id);
              setSessionContainers(Array.isArray(contents?.containers) ? contents.containers : []);
              setSessions(Array.isArray(contents?.sessions) ? contents.sessions : []);
            } else {
              // Load root level items
              const [containerData, sessionData] = await Promise.all([
                sessionContainerService.getContainers(null),
                sessionService.getAll()
              ]);
              const validContainerData = Array.isArray(containerData) ? containerData : [];
              const validSessionData = Array.isArray(sessionData) ? sessionData : [];
              const rootSessions = validSessionData.filter(s => !s.container);
              setSessionContainers(validContainerData);
              setSessions(rootSessions);
            }
            break;

          case 4: // Vidéos
            if (currentVideoContainer) {
              // Load contents of current container
              const contents = await videoContainerService.getContents(currentVideoContainer.id);
              setVideoContainers(Array.isArray(contents?.containers) ? contents.containers : []);
              setVideos(Array.isArray(contents?.videos) ? contents.videos : []);
            } else {
              // Load root level items
              const [containerData, videoData] = await Promise.all([
                videoContainerService.getContainers(null),
                videoService.getAll()
              ]);
              const validContainerData = Array.isArray(containerData) ? containerData : [];
              const validVideoData = Array.isArray(videoData) ? videoData : [];
              const rootVideos = validVideoData.filter(v => !v.container);
              setVideoContainers(validContainerData);
              setVideos(rootVideos);
            }
            break;

          case 5: // Enseignants
            if (teachers.length === 0) {
              const data = await userService.getTeachers();
              setTeachers(data);
            }
            break;
        }
      } catch (error) {
        console.error('Error fetching navigation content:', error);
        toast.error('Erreur lors du chargement du contenu');
      }
    };
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightTabValue, currentCourseContainer, currentPatternContainer, currentExerciseContainer, currentSessionContainer, currentVideoContainer]);

  // Load timeline items when selected student changes (or after « Créer la séance »)
  const [timelineReload, setTimelineReload] = useState(0);
  useEffect(() => {
    const loadTimelineItems = async () => {
      if (!selectedStudent) {
        setTimelineItems([]);
        return;
      }

      const sessionId = studentSessionMap.get(selectedStudent.id);
      if (!sessionId) {
        setTimelineItems([]);
        return;
      }

      try {
        const items = await activeSessionService.getItemsBySession(sessionId);

        // Load quick recap data for courses and videos
        const extendedItems: ExtendedSessionItem[] = await Promise.all(
          items.map(async (item) => {
            let quick_recap_course = null;
            let quick_recap_course_title = null;
            let quick_recap_course_pdf_url = null;
            let quick_recap_video = null;
            let quick_recap_video_title = null;
            let quick_recap_video_url = null;

            // Load quick recap data for courses
            if (item.item_type === 'course' && item.course) {
              try {
                const courseData = await courseService.getCourse(item.course);
                quick_recap_course = courseData.quick_recap_course || null;
                quick_recap_course_title = courseData.quick_recap_course_title || null;
                quick_recap_video = courseData.quick_recap_video || null;
                quick_recap_video_title = courseData.quick_recap_video_title || null;

                // Load the actual PDF URL and video URL from the references
                if (quick_recap_course) {
                  try {
                    const refCourseData = await courseService.getCourse(quick_recap_course);
                    quick_recap_course_pdf_url = refCourseData.preview_pdf_url || null;
                  } catch (error) {
                    console.error('Error loading reference course PDF:', error);
                  }
                }
                if (quick_recap_video) {
                  try {
                    const refVideoData = await videoService.getVideo(quick_recap_video);
                    quick_recap_video_url = refVideoData.video_file_url || refVideoData.url || null;
                  } catch (error) {
                    console.error('Error loading reference video URL:', error);
                  }
                }
              } catch (error) {
                console.error('Error loading course quick recap:', error);
              }
            }

            // Load quick recap data for videos
            if (item.item_type === 'video' && item.video) {
              try {
                const videoData = await videoService.getVideo(item.video);
                quick_recap_course = videoData.quick_recap_course || null;
                quick_recap_course_title = videoData.quick_recap_course_title || null;
                quick_recap_video = videoData.quick_recap_video || null;
                quick_recap_video_title = videoData.quick_recap_video_title || null;

                // Load the actual PDF URL and video URL from the references
                if (quick_recap_course) {
                  try {
                    const refCourseData = await courseService.getCourse(quick_recap_course);
                    quick_recap_course_pdf_url = refCourseData.preview_pdf_url || null;
                  } catch (error) {
                    console.error('Error loading reference course PDF:', error);
                  }
                }
                if (quick_recap_video) {
                  try {
                    const refVideoData = await videoService.getVideo(quick_recap_video);
                    quick_recap_video_url = refVideoData.video_file_url || refVideoData.url || null;
                  } catch (error) {
                    console.error('Error loading reference video URL:', error);
                  }
                }
              } catch (error) {
                console.error('Error loading video quick recap:', error);
              }
            }

            return {
              id: item.id || Date.now(),
              item_type: item.item_type,
              course: item.course || null,
              course_title: item.course_title,
              course_thumbnail_url: item.course_thumbnail_url,
              exercise: item.exercise || null,
              exercise_pattern: item.exercise_pattern_title,
              exercise_pattern_thumbnail_url: item.exercise_pattern_thumbnail_url,
              video: item.video || null,
              video_title: item.video_title,
              video_thumbnail_url: item.video_thumbnail_url,
              order: item.order,
              quick_recap_course,
              quick_recap_course_title,
              quick_recap_course_pdf_url,
              quick_recap_video,
              quick_recap_video_title,
              quick_recap_video_url,
            };
          })
        );

        setTimelineItems(extendedItems);
      } catch (error) {
        console.error('Error loading timeline items:', error);
        setTimelineItems([]);
      }
    };

    loadTimelineItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent, studentSessionMap, timelineReload]);

  // Get current time in teacher's timezone
  const getCurrentTimeInTeacherTimezone = (): Dayjs => {
    const teacherTimezone = user?.timezone || 'Pacific/Noumea';
    return dayjs().tz(teacherTimezone);
  };

  const handleAddStudent = async (student: Student) => {
    if (!activeStudents.find(s => s.id === student.id)) {
      const newActiveStudent: ActiveStudent = {
        ...student,
        order: activeStudents.length,
        arrival_time: getCurrentTimeInTeacherTimezone(),
      };

      try {
        // Create active session in backend
        const session = await activeSessionService.create({
          student: student.id,
          arrival_time: newActiveStudent.arrival_time.toISOString(),
          is_active: true,
        });

        // Create seat assignment (backend will auto-assign next available seat and clean up stale ones)
        let seatAssignment;
        try {
          seatAssignment = await seatAssignmentService.create({
            student: student.id,
            seat_number: 0, // Backend will auto-assign
          });
        } catch (seatError) {
          console.error('Error creating seat assignment:', seatError);
        }

        // Update new student with seat info
        if (seatAssignment) {
          newActiveStudent.seat_number = seatAssignment.seat_number;
          newActiveStudent.seat_assignment_id = seatAssignment.id;
        }

        // Update session map
        if (session.id) {
          setStudentSessionMap(prev => new Map(prev).set(student.id, session.id!));
        }

        setActiveStudents([...activeStudents, newActiveStudent]);
        // Remove from suggestions
        setSuggestedStudents(suggestedStudents.filter(s => s.id !== student.id));
        // Select the newly added student
        setSelectedStudent(newActiveStudent);
        // Clear timeline for new student
        setTimelineItems([]);
      } catch (error) {
        console.error('Error creating active session:', error);
        toast.error('Erreur lors de l\'ajout de l\'élève');
      }
    }
  };

  // Seat configuration helpers
  const saveSeatConfig = (seats: number[]) => {
    const sorted = [...seats].sort((a, b) => a - b);
    setAvailableSeats(sorted);
    localStorage.setItem('teacher_available_seats', JSON.stringify(sorted));
  };

  const handleAddSeat = () => {
    const num = parseInt(newSeatInput);
    if (isNaN(num) || num < 1) return;
    if (availableSeats.includes(num)) {
      toast.error(`La place ${num} existe déjà`);
      return;
    }
    saveSeatConfig([...availableSeats, num]);
    setNewSeatInput('');
  };

  const handleRemoveSeat = (seat: number) => {
    saveSeatConfig(availableSeats.filter(s => s !== seat));
  };

  const handleResetSeats = () => {
    saveSeatConfig(DEFAULT_SEATS);
  };

  const handleUpdateSeatNumber = async (student: ActiveStudent, newSeatNumber: number) => {
    if (!student.seat_assignment_id || !newSeatNumber || newSeatNumber < 1) return;

    try {
      await seatAssignmentService.update(student.seat_assignment_id, {
        seat_number: newSeatNumber,
      });

      // Refresh all seat assignments to catch any swaps
      const seatAssignments = await seatAssignmentService.getForTeacher();
      const seatMap = new Map<number, SeatAssignment>();
      seatAssignments.forEach(a => seatMap.set(a.student, a));

      setActiveStudents(prev => prev.map(s => {
        const seat = seatMap.get(s.id);
        return { ...s, seat_number: seat?.seat_number, seat_assignment_id: seat?.id };
      }));

      if (selectedStudent) {
        const selectedSeat = seatMap.get(selectedStudent.id);
        setSelectedStudent({ ...selectedStudent, seat_number: selectedSeat?.seat_number, seat_assignment_id: selectedSeat?.id });
      }

      toast.success('Numéro de place mis à jour');
    } catch (error) {
      console.error('Error updating seat number:', error);
      toast.error('Erreur lors de la mise à jour du numéro de place');
    }
  };

  const handleRemoveStudent = async (studentId: number) => {
    const removed = activeStudents.find(s => s.id === studentId);

    try {
      // Delete seat assignment (backend also handles this on session delete, but clean up explicitly)
      if (removed?.seat_assignment_id) {
        try {
          await seatAssignmentService.delete(removed.seat_assignment_id);
        } catch (e) {
          // Ignore - backend will clean up on session delete
        }
      }

      // Delete or end session in backend
      const sessionId = studentSessionMap.get(studentId);
      if (sessionId) {
        await activeSessionService.delete(sessionId);
        // Remove from session map
        setStudentSessionMap(prev => {
          const newMap = new Map(prev);
          newMap.delete(studentId);
          return newMap;
        });
      }

      setActiveStudents(activeStudents.filter(s => s.id !== studentId));
      if (removed && removed.id === selectedStudent?.id) {
        setSelectedStudent(null);
        setTimelineItems([]);
      }
      // Add back to suggestions if not already there
      if (removed && !suggestedStudents.find(s => s.id === removed.id)) {
        setSuggestedStudents([...suggestedStudents, removed]);
      }
    } catch (error) {
      console.error('Error removing student session:', error);
      toast.error('Erreur lors de la suppression de l\'élève');
    }
  };

  const handleDragStart = (student: Student) => {
    setDraggedStudent(student);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedStudent) {
      handleAddStudent(draggedStudent);
      setDraggedStudent(null);
    }
  };

  const handleSearchSelect = (student: Student | null) => {
    if (student) {
      handleAddStudent(student);
      // Use setTimeout to ensure the clear happens after Autocomplete's internal updates
      setTimeout(() => {
        setSearchValue(null);
        setInputValue('');
      }, 0);
    } else {
      setSearchValue(null);
      setInputValue('');
    }
  };

  // Round time down to previous 15 minutes (or subtract 15 if already on a quarter)
  const roundQuarterDown = (time: Dayjs): Dayjs => {
    const minutes = time.minute();
    const isOnQuarter = minutes % 15 === 0;

    if (isOnQuarter) {
      // Already on a quarter, subtract 15 minutes
      return time.subtract(15, 'minute').second(0);
    } else {
      // Round down to previous quarter
      const roundedMinutes = Math.floor(minutes / 15) * 15;
      return time.minute(roundedMinutes).second(0);
    }
  };

  // Round time up to next 15 minutes (or add 15 if already on a quarter)
  const roundQuarterUp = (time: Dayjs): Dayjs => {
    const minutes = time.minute();
    const isOnQuarter = minutes % 15 === 0;

    if (isOnQuarter) {
      // Already on a quarter, add 15 minutes
      return time.add(15, 'minute').second(0);
    } else {
      // Round up to next quarter
      const roundedMinutes = Math.ceil(minutes / 15) * 15;
      return time.minute(roundedMinutes).second(0);
    }
  };

  // Round arrival time of a student down
  const handleRoundArrivalTimeDown = (student: ActiveStudent) => {
    const rounded = roundQuarterDown(student.arrival_time);
    setActiveStudents(activeStudents.map(s =>
      s.id === student.id ? { ...s, arrival_time: rounded } : s
    ));
    if (selectedStudent?.id === student.id) {
      setSelectedStudent({ ...student, arrival_time: rounded });
    }
  };

  // Round arrival time of a student up
  const handleRoundArrivalTimeUp = (student: ActiveStudent) => {
    const rounded = roundQuarterUp(student.arrival_time);
    setActiveStudents(activeStudents.map(s =>
      s.id === student.id ? { ...s, arrival_time: rounded } : s
    ));
    if (selectedStudent?.id === student.id) {
      setSelectedStudent({ ...student, arrival_time: rounded });
    }
  };

  // Open end session dialog
  const handleOpenEndSession = (student: ActiveStudent) => {
    setStudentToEnd(student);
    setEndTime(getCurrentTimeInTeacherTimezone());
    setDiscount('');
    setDiscountLabel('');
    calculatePrice(student.arrival_time, getCurrentTimeInTeacherTimezone());
    setEndSessionDialogOpen(true);
  };

  // Calculate session price
  const calculatePrice = (startTime: Dayjs, endTime: Dayjs) => {
    // Normalize times to remove seconds and milliseconds for accurate calculation
    const startTimeNormalized = startTime.startOf('minute');
    const endTimeNormalized = endTime.startOf('minute');

    const durationInMinutes = endTimeNormalized.diff(startTimeNormalized, 'minute');
    const durationInHours = durationInMinutes / 60;
    const calculatedPrice = Math.round(durationInHours * 3000);
    setSessionPrice(calculatedPrice.toString());
  };

  // Handle start time change
  const handleStartTimeChange = (newValue: Dayjs | null) => {
    if (newValue && studentToEnd && endTime) {
      const updatedStudent = { ...studentToEnd, arrival_time: newValue };
      setStudentToEnd(updatedStudent);
      calculatePrice(newValue, endTime);
    }
  };

  // Handle end time change
  const handleEndTimeChange = (newValue: Dayjs | null) => {
    if (newValue) {
      setEndTime(newValue);
      if (studentToEnd) {
        calculatePrice(studentToEnd.arrival_time, newValue);
      }
    }
  };

  // Round start time down to quarter
  const handleRoundStartTimeDown = () => {
    if (studentToEnd) {
      const rounded = roundQuarterDown(studentToEnd.arrival_time);
      const updatedStudent = { ...studentToEnd, arrival_time: rounded };
      setStudentToEnd(updatedStudent);
      if (endTime) {
        calculatePrice(rounded, endTime);
      }
    }
  };

  // Round start time up to quarter
  const handleRoundStartTimeUp = () => {
    if (studentToEnd) {
      const rounded = roundQuarterUp(studentToEnd.arrival_time);
      const updatedStudent = { ...studentToEnd, arrival_time: rounded };
      setStudentToEnd(updatedStudent);
      if (endTime) {
        calculatePrice(rounded, endTime);
      }
    }
  };

  // Round end time down to quarter
  const handleRoundEndTimeDown = () => {
    if (endTime) {
      const rounded = roundQuarterDown(endTime);
      setEndTime(rounded);
      if (studentToEnd) {
        calculatePrice(studentToEnd.arrival_time, rounded);
      }
    }
  };

  // Round end time up to quarter
  const handleRoundEndTimeUp = () => {
    if (endTime) {
      const rounded = roundQuarterUp(endTime);
      setEndTime(rounded);
      if (studentToEnd) {
        calculatePrice(studentToEnd.arrival_time, rounded);
      }
    }
  };

  // Create invoice
  const handleCreateInvoice = async (status: 'draft' | 'issued') => {
    if (!studentToEnd || !endTime) {
      toast.error('Informations de séance manquantes');
      return;
    }

    if (!sessionPrice || parseFloat(sessionPrice) <= 0) {
      toast.error('Le prix doit être supérieur à 0');
      return;
    }

    try {
      await invoiceService.create({
        student: studentToEnd.id,
        date: getCurrentTimeInTeacherTimezone().format('YYYY-MM-DD'),
        start_time: studentToEnd.arrival_time.format('HH:mm:ss'),
        end_time: endTime.format('HH:mm:ss'),
        price: parseFloat(sessionPrice),
        discount: discount ? parseFloat(discount) : undefined,
        discount_label: discountLabel || undefined,
        status: status,
      });

      const statusMessage = status === 'draft' ? 'Facture ajoutée aux factures avant émission' : 'Facture émise avec succès';
      toast.success(statusMessage);

      // Remove student from active list
      handleRemoveStudent(studentToEnd.id);
      setEndSessionDialogOpen(false);
      setStudentToEnd(null);
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Erreur lors de la création de la facture');
    }
  };

  // Handle drag start to track active item
  const handleTimelineDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeIdStr = active.id.toString();
    setActiveId(activeIdStr);

    // Find the item being dragged
    if (activeIdStr.startsWith('nav-')) {
      const [, type, idStr] = activeIdStr.split('-');
      const itemId = parseInt(idStr);

      let item = null;
      if (type === 'course') {
        item = courses.find(c => c.id === itemId) ||
               teacherCourses.find(c => c.id === itemId) ||
               (recentlyIntegrated.find(r => r.type === 'course' && r.itemId === itemId) ?
                 { id: itemId, title: recentlyIntegrated.find(r => r.type === 'course' && r.itemId === itemId)!.title,
                   thumbnail_url: recentlyIntegrated.find(r => r.type === 'course' && r.itemId === itemId)!.thumbnail_url } : null);
      } else if (type === 'exercise') {
        item = exercises.find(e => e.id === itemId) ||
               teacherExercises.find(e => e.id === itemId) ||
               (recentlyIntegrated.find(r => r.type === 'exercise' && r.itemId === itemId) ?
                 { id: itemId, pattern_title: recentlyIntegrated.find(r => r.type === 'exercise' && r.itemId === itemId)!.title,
                   pattern_thumbnail_url: recentlyIntegrated.find(r => r.type === 'exercise' && r.itemId === itemId)!.thumbnail_url } : null);
      } else if (type === 'video') {
        item = videos.find(v => v.id === itemId) ||
               teacherVideos.find(v => v.id === itemId) ||
               (recentlyIntegrated.find(r => r.type === 'video' && r.itemId === itemId) ?
                 { id: itemId, title: recentlyIntegrated.find(r => r.type === 'video' && r.itemId === itemId)!.title,
                   thumbnail_url: recentlyIntegrated.find(r => r.type === 'video' && r.itemId === itemId)!.thumbnail_url } : null);
      } else if (type === 'session') {
        item = sessions.find(s => s.id === itemId) || teacherSessions.find(s => s.id === itemId);
      }

      if (item) {
        setActiveDragItem({ type: type as any, data: item });
      }
    } else if (activeIdStr.startsWith('timeline-item-')) {
      // Find timeline item
      const itemId = parseInt(activeIdStr.replace('timeline-item-', ''));
      const item = timelineItems.find(i => i.id === itemId);
      if (item) {
        setActiveDragItem({ type: 'timeline', data: item });
      }
    }
  };

  // Handle drag over to show insertion preview
  const handleTimelineDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) {
      setOverId(null);
      return;
    }

    setActiveId(active.id.toString());
    setOverId(over.id.toString());
  };

  // Handle drag end for timeline reordering and insertion (using @dnd-kit)
  const handleTimelineDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setOverId(null);
    setActiveDragItem(null);

    if (!over || !selectedStudent) return;

    const sessionId = studentSessionMap.get(selectedStudent.id);
    if (!sessionId) {
      toast.error('Session introuvable');
      return;
    }

    // Extract IDs
    const activeIdStr = active.id.toString();
    const overIdStr = over.id.toString();

    // Handle drop on the timeline drop area itself (when timeline is empty or dropping at the end)
    if (overIdStr === 'timeline-drop-area' && activeIdStr.startsWith('nav-')) {
      const [, type, idStr] = activeIdStr.split('-');
      const itemId = parseInt(idStr);
      const insertIndex = timelineItems.length; // Add at the end

      // Handle composite pattern drops - open modal
      if (type === 'compositepattern') {
        const cp = compositePatterns.find(c => c.id === itemId);
        if (cp) {
          try {
            const fullCp = await compositePatternService.get(cp.id);
            setPendingPatternDropIndex(insertIndex);
            setSelectedCompositePattern(fullCp);
            setCompositeModalOpen(true);
          } catch (error) {
            console.error('Error loading composite pattern:', error);
            toast.error('Erreur lors du chargement du pattern composé');
          }
        }
        return;
      }

      // Handle pattern drops - open modal
      if (type === 'pattern') {
        const pattern = patterns.find(p => p.id === itemId);
        if (pattern) {
          setPendingPatternDropIndex(insertIndex);
          setSelectedPatternForInstances(pattern);
          setPatternInstanceModalOpen(true);
        }
        return;
      }

      try {
        if (type === 'session') {
          // Handle session drops
          const fullSession = await sessionService.getSession(itemId);

          if (!fullSession.items || fullSession.items.length === 0) {
            toast.info('Cette séance ne contient aucun élément');
            return;
          }

          const newItems: ExtendedSessionItem[] = [];

          for (let index = 0; index < fullSession.items.length; index++) {
            const sessionItem = fullSession.items[index];
            const backendItem = await activeSessionService.createItem({
              session: sessionId,
              item_type: sessionItem.item_type,
              course: sessionItem.course,
              exercise: sessionItem.exercise,
              video: sessionItem.video,
              order: insertIndex + index,
            });

            newItems.push({
              id: backendItem.id || Date.now() + index,
              item_type: sessionItem.item_type,
              course: sessionItem.course,
              course_title: backendItem.course_title,
              course_thumbnail_url: backendItem.course_thumbnail_url,
              exercise: sessionItem.exercise,
              exercise_pattern: backendItem.exercise_pattern_title,
              exercise_pattern_thumbnail_url: backendItem.exercise_pattern_thumbnail_url,
              video: sessionItem.video,
              video_title: backendItem.video_title,
              video_thumbnail_url: backendItem.video_thumbnail_url,
              order: insertIndex + index,
            });
          }

          setTimelineItems([...timelineItems, ...newItems]);

          // Log action: session added to student's timeline
          try {
            console.log('Logging session added:', { studentId: selectedStudent.id, sessionId: itemId });
            const result = await studentActionService.logSessionAdded(selectedStudent.id, itemId);
            console.log('Session added action logged successfully:', result);

            // Reload actions if the exchanges modal is open
            if (exchangesDialogOpen) {
              loadStudentActions();
            }
          } catch (error) {
            console.error('Error logging session added action:', error);
            toast.error('Erreur lors de l\'enregistrement de l\'action');
          }
        } else {
          // Handle single items
          const backendItem = await activeSessionService.createItem({
            session: sessionId,
            item_type: type as 'course' | 'exercise' | 'video',
            course: type === 'course' ? itemId : null,
            exercise: type === 'exercise' ? itemId : null,
            video: type === 'video' ? itemId : null,
            order: insertIndex,
          });

          const newItem: ExtendedSessionItem = {
            id: backendItem.id || Date.now(),
            item_type: type as 'course' | 'exercise' | 'video',
            course: type === 'course' ? itemId : null,
            course_title: backendItem.course_title,
            course_thumbnail_url: backendItem.course_thumbnail_url,
            exercise: type === 'exercise' ? itemId : null,
            exercise_pattern: backendItem.exercise_pattern_title,
            exercise_pattern_thumbnail_url: backendItem.exercise_pattern_thumbnail_url,
            video: type === 'video' ? itemId : null,
            video_title: backendItem.video_title,
            video_thumbnail_url: backendItem.video_thumbnail_url,
            order: insertIndex,
          };

          setTimelineItems([...timelineItems, newItem]);

          // Add to recently integrated
          const title = type === 'course' ? backendItem.course_title :
                       type === 'exercise' ? backendItem.exercise_pattern_title :
                       backendItem.video_title;
          const thumbnail = type === 'course' ? backendItem.course_thumbnail_url :
                          type === 'exercise' ? backendItem.exercise_pattern_thumbnail_url :
                          backendItem.video_thumbnail_url;
          addToRecentlyIntegrated(type as 'course' | 'exercise' | 'video', itemId, title || '', thumbnail);
        }
      } catch (error) {
        console.error('Error adding timeline item:', error);
        toast.error('Erreur lors de l\'ajout à la trame');
      }
      return;
    }

    // Check if dragging from navigation (course-, exercise-, video-, session-)
    const isFromNavigation = activeIdStr.startsWith('nav-');
    const isTimelineItem = activeIdStr.startsWith('timeline-item-');

    if (isTimelineItem) {
      // Reordering within timeline
      if (activeIdStr === overIdStr) return;

      const oldIndex = timelineItems.findIndex(item => `timeline-item-${item.id}` === activeIdStr);
      const newIndex = timelineItems.findIndex(item => `timeline-item-${item.id}` === overIdStr);

      if (oldIndex === -1 || newIndex === -1) return;

      // Reorder using arrayMove from @dnd-kit
      const reorderedItems = arrayMove(timelineItems, oldIndex, newIndex).map((item, index) => ({
        ...item,
        order: index,
      }));

      setTimelineItems(reorderedItems);

      // Update backend
      try {
        await activeSessionService.reorderItems(
          sessionId,
          reorderedItems.map((item, index) => ({ id: item.id, order: index }))
        );
      } catch (error) {
        console.error('Error reordering timeline items:', error);
        toast.error('Erreur lors de la réorganisation');
        // Revert on error
        setTimelineItems(timelineItems);
      }
    } else if (isFromNavigation) {
      // Inserting new item from navigation
      const [, type, idStr] = activeIdStr.split('-'); // nav-course-123, nav-session-456
      const itemId = parseInt(idStr);

      // Calculate insertion index
      let insertIndex = timelineItems.length;
      if (overIdStr.startsWith('timeline-item-')) {
        const overIndex = timelineItems.findIndex(item => `timeline-item-${item.id}` === overIdStr);
        if (overIndex !== -1) {
          insertIndex = overIndex;
        }
      }

      // Handle composite pattern drops - open modal
      if (type === 'compositepattern') {
        const cp = compositePatterns.find(c => c.id === itemId);
        if (cp) {
          try {
            const fullCp = await compositePatternService.get(cp.id);
            setPendingPatternDropIndex(insertIndex);
            setSelectedCompositePattern(fullCp);
            setCompositeModalOpen(true);
          } catch (error) {
            console.error('Error loading composite pattern:', error);
            toast.error('Erreur lors du chargement du pattern composé');
          }
        }
        return;
      }

      // Handle pattern drops - open modal
      if (type === 'pattern') {
        const pattern = patterns.find(p => p.id === itemId);
        if (pattern) {
          setPendingPatternDropIndex(insertIndex);
          setSelectedPatternForInstances(pattern);
          setPatternInstanceModalOpen(true);
        }
        return;
      }

      try {
        // Handle sessions specially - add all their items
        if (type === 'session') {
          // Fetch the full session with items
          const fullSession = await sessionService.getSession(itemId);

          if (!fullSession.items || fullSession.items.length === 0) {
            toast.info('Cette séance ne contient aucun élément');
            return;
          }

          const newItems: ExtendedSessionItem[] = [];

          for (let index = 0; index < fullSession.items.length; index++) {
            const sessionItem = fullSession.items[index];
            const backendItem = await activeSessionService.createItem({
              session: sessionId,
              item_type: sessionItem.item_type,
              course: sessionItem.course,
              exercise: sessionItem.exercise,
              video: sessionItem.video,
              order: insertIndex + index,
            });

            newItems.push({
              id: backendItem.id || Date.now() + index,
              item_type: sessionItem.item_type,
              course: sessionItem.course,
              course_title: backendItem.course_title,
              course_thumbnail_url: backendItem.course_thumbnail_url,
              exercise: sessionItem.exercise,
              exercise_pattern: backendItem.exercise_pattern_title,
              exercise_pattern_thumbnail_url: backendItem.exercise_pattern_thumbnail_url,
              video: sessionItem.video,
              video_title: backendItem.video_title,
              video_thumbnail_url: backendItem.video_thumbnail_url,
              order: insertIndex + index,
            });
          }

          // Insert all session items at the specified position
          const updatedItems = [...timelineItems];
          updatedItems.splice(insertIndex, 0, ...newItems);

          // Reindex all items
          const reindexedItems = updatedItems.map((item, index) => ({ ...item, order: index }));
          setTimelineItems(reindexedItems);

          // Update backend order for all items
          try {
            await activeSessionService.reorderItems(
              sessionId,
              reindexedItems.map((item, index) => ({ id: item.id, order: index }))
            );
          } catch (error) {
            console.error('Error updating order after session insertion:', error);
          }
        } else {
          // Handle single items (course, exercise, video)
          const backendItem = await activeSessionService.createItem({
            session: sessionId,
            item_type: type as 'course' | 'exercise' | 'video',
            course: type === 'course' ? itemId : null,
            exercise: type === 'exercise' ? itemId : null,
            video: type === 'video' ? itemId : null,
            order: insertIndex,
          });

          const newItem: ExtendedSessionItem = {
            id: backendItem.id || Date.now(),
            item_type: type as 'course' | 'exercise' | 'video',
            course: type === 'course' ? itemId : null,
            course_title: backendItem.course_title,
            course_thumbnail_url: backendItem.course_thumbnail_url,
            exercise: type === 'exercise' ? itemId : null,
            exercise_pattern: backendItem.exercise_pattern_title,
            exercise_pattern_thumbnail_url: backendItem.exercise_pattern_thumbnail_url,
            video: type === 'video' ? itemId : null,
            video_title: backendItem.video_title,
            video_thumbnail_url: backendItem.video_thumbnail_url,
            order: insertIndex,
          };

          // Insert at the specified position
          const updatedItems = [...timelineItems];
          updatedItems.splice(insertIndex, 0, newItem);

          // Reindex all items
          const reindexedItems = updatedItems.map((item, index) => ({ ...item, order: index }));
          setTimelineItems(reindexedItems);

          // Add to recently integrated
          const title = type === 'course' ? backendItem.course_title :
                       type === 'exercise' ? backendItem.exercise_pattern_title :
                       backendItem.video_title;
          const thumbnail = type === 'course' ? backendItem.course_thumbnail_url :
                          type === 'exercise' ? backendItem.exercise_pattern_thumbnail_url :
                          backendItem.video_thumbnail_url;
          addToRecentlyIntegrated(type as 'course' | 'exercise' | 'video', itemId, title || '', thumbnail);

          // Update backend order for all items
          try {
            await activeSessionService.reorderItems(
              sessionId,
              reindexedItems.map((item, index) => ({ id: item.id, order: index }))
            );
          } catch (error) {
            console.error('Error updating order after insertion:', error);
          }
        }
      } catch (error) {
        console.error('Error adding timeline item:', error);
        toast.error('Erreur lors de l\'ajout à la trame');
      }
    }
  };

  // Remove item from timeline
  const handleRemoveTimelineItem = async (itemId: number) => {
    try {
      await activeSessionService.deleteItem(itemId);
      setTimelineItems(timelineItems.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error removing timeline item:', error);
      toast.error('Erreur lors de la suppression de l\'élément');
    }
  };

  // Handle pattern instance confirmation
  const handlePatternInstanceConfirm = async (count: number, containerId: number | null) => {
    if (!selectedStudent || !selectedPatternForInstances || pendingPatternDropIndex === null) {
      return;
    }

    const sessionId = studentSessionMap.get(selectedStudent.id);
    if (!sessionId) {
      toast.error('Session introuvable');
      return;
    }

    try {
      // Step 1: Ensure the container exists or create it
      let targetContainerId = containerId;
      if (!targetContainerId) {
        const allContainers = await exerciseContainerService.getContainers(null);
        let defaultContainer = allContainers.find(
          (c: any) => c.title === 'Exercices générés à la volée pour peupler une séance'
        );

        if (!defaultContainer) {
          defaultContainer = await exerciseContainerService.createContainer({
            title: 'Exercices générés à la volée pour peupler une séance',
            parent: null,
          });
          toast.info('Container par défaut créé');
        }
        targetContainerId = defaultContainer.id;
      }

      // Step 2: Create exercises from the pattern
      const createdExercises = [];
      for (let i = 0; i < count; i++) {
        const createdExercise = await exerciseService.createExerciseFromPattern(selectedPatternForInstances.id);
        await exerciseService.updateExercise(createdExercise.id, { container: targetContainerId });
        const updatedExercise = await exerciseService.getExercise(createdExercise.id);
        createdExercises.push(updatedExercise);
      }

      toast.success(`${count} exercice${count > 1 ? 's' : ''} créé${count > 1 ? 's' : ''}`);

      // Step 3: Add exercises to the timeline at the desired position
      const newItems: ExtendedSessionItem[] = [];
      for (let i = 0; i < createdExercises.length; i++) {
        const exercise = createdExercises[i];
        const backendItem = await activeSessionService.createItem({
          session: sessionId,
          item_type: 'exercise' as const,
          exercise: exercise.id,
          order: pendingPatternDropIndex + i,
        });

        newItems.push({
          id: backendItem.id || Date.now() + i,
          item_type: 'exercise',
          course: null,
          course_title: undefined,
          exercise: exercise.id,
          exercise_pattern: backendItem.exercise_pattern_title,
          exercise_pattern_thumbnail_url: backendItem.exercise_pattern_thumbnail_url,
          video: null,
          video_title: undefined,
          video_thumbnail_url: undefined,
          order: pendingPatternDropIndex + i,
        });
      }

      // Step 4: Insert items at the specified position
      const updatedItems = [...timelineItems];
      updatedItems.splice(pendingPatternDropIndex, 0, ...newItems);

      // Reindex all items
      const reindexedItems = updatedItems.map((item, index) => ({ ...item, order: index }));
      setTimelineItems(reindexedItems);

      // Update backend order for all items
      try {
        await activeSessionService.reorderItems(
          sessionId,
          reindexedItems.map((item, index) => ({ id: item.id, order: index }))
        );
      } catch (error) {
        console.error('Error updating order after pattern insertion:', error);
      }

      toast.success(`Exercices ajoutés à la trame à la position ${pendingPatternDropIndex + 1}`);
    } catch (error) {
      console.error('Error creating exercises from pattern:', error);
      toast.error('Erreur lors de la création des exercices');
    } finally {
      setPatternInstanceModalOpen(false);
      setSelectedPatternForInstances(null);
      setPendingPatternDropIndex(null);
    }
  };

  const handleCompositePatternConfirm = async (
    selectedPatternIds: number[],
    countsPerPattern: Record<number, number>,
    containerId: number | null
  ) => {
    if (!selectedStudent || pendingPatternDropIndex === null) return;

    const sessionId = studentSessionMap.get(selectedStudent.id);
    if (!sessionId) {
      toast.error('Session introuvable');
      return;
    }

    try {
      let targetContainerId = containerId;
      if (!targetContainerId) {
        const allContainers = await exerciseContainerService.getContainers(null);
        let defaultContainer = allContainers.find(
          (c: any) => c.title === 'Exercices générés à la volée pour peupler une séance'
        );
        if (!defaultContainer) {
          defaultContainer = await exerciseContainerService.createContainer({
            title: 'Exercices générés à la volée pour peupler une séance',
            parent: null,
          });
        }
        targetContainerId = defaultContainer.id;
      }

      const newItems: ExtendedSessionItem[] = [];
      let insertIdx = pendingPatternDropIndex;
      for (const patternId of selectedPatternIds) {
        const count = countsPerPattern[patternId] || 1;
        for (let i = 0; i < count; i++) {
          const createdExercise = await exerciseService.createExerciseFromPattern(patternId);
          await exerciseService.updateExercise(createdExercise.id, { container: targetContainerId });
          const backendItem = await activeSessionService.createItem({
            session: sessionId,
            item_type: 'exercise' as const,
            exercise: createdExercise.id,
            order: insertIdx,
          });
          newItems.push({
            id: backendItem.id || Date.now() + insertIdx,
            item_type: 'exercise',
            course: null,
            course_title: undefined,
            exercise: createdExercise.id,
            exercise_pattern: backendItem.exercise_pattern_title,
            exercise_pattern_thumbnail_url: backendItem.exercise_pattern_thumbnail_url,
            video: null,
            video_title: undefined,
            video_thumbnail_url: undefined,
            order: insertIdx,
          });
          insertIdx++;
        }
      }

      const updatedItems = [...timelineItems];
      updatedItems.splice(pendingPatternDropIndex, 0, ...newItems);
      const reindexedItems = updatedItems.map((item, index) => ({ ...item, order: index }));
      setTimelineItems(reindexedItems);

      try {
        await activeSessionService.reorderItems(
          sessionId,
          reindexedItems.map((item, index) => ({ id: item.id, order: index }))
        );
      } catch (error) {
        console.error('Error updating order:', error);
      }

      toast.success(`${newItems.length} exercice${newItems.length > 1 ? 's' : ''} ajouté${newItems.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error creating exercises from composite pattern:', error);
      toast.error('Erreur lors de la création des exercices');
    } finally {
      setCompositeModalOpen(false);
      setSelectedCompositePattern(null);
      setPendingPatternDropIndex(null);
    }
  };

  // Open quick recap modal
  const handleOpenQuickRecap = (item: ExtendedSessionItem) => {
    setQuickRecapItem(item);
    setQuickRecapDialogOpen(true);
  };

  // Open exercise evaluation modal
  const handleOpenEvaluationModal = async (item: ExtendedSessionItem) => {
    setExerciseToEvaluate(item);

    // Load exercise data to get pattern_id
    if (item.exercise) {
      try {
        const exerciseData = await exerciseService.getExercise(item.exercise);
        setExercisePatternId(exerciseData.pattern);
      } catch (error) {
        console.error('Error loading exercise data:', error);
        setExercisePatternId(undefined);
      }
    }

    setEvaluationModalOpen(true);
  };

  // Save exercise evaluation
  const handleSaveEvaluation = async (evaluation: ExerciseEvaluation) => {
    if (!selectedStudent) {
      toast.error('Aucun élève sélectionné');
      return;
    }

    try {
      const evaluationData = {
        student: selectedStudent.id,
        exercise: evaluation.exerciseId,
        status: evaluation.status,
        rating: evaluation.rating,
        remarks: evaluation.remarks,
        selected_solution: evaluation.selectedSolutionId,
        implemented_skill_ids: evaluation.implementedSkills,
        poorly_executed_skill_ids: evaluation.poorlyExecutedSkills,
        missing_justifications: evaluation.missingJustifications,
      };

      await exerciseEvaluationService.create(evaluationData);
      toast.success('Évaluation enregistrée avec succès');

      // Reload actions if the exchanges modal is open
      if (exchangesDialogOpen) {
        loadStudentActions();
      }
    } catch (error) {
      console.error('Error saving evaluation:', error);
      toast.error('Erreur lors de l\'enregistrement de l\'évaluation');
    }
  };

  // Load student actions for the exchanges modal
  const loadStudentActions = async () => {
    if (!selectedStudent) return;

    try {
      setLoadingActions(true);
      const actions = await studentActionService.getAll({ student: selectedStudent.id });
      setStudentActions(actions);
    } catch (error) {
      console.error('Error loading student actions:', error);
      toast.error('Erreur lors du chargement des actions');
    } finally {
      setLoadingActions(false);
    }
  };

  // Open exchanges dialog and load actions
  const handleOpenExchanges = () => {
    setExchangesDialogOpen(true);
    loadStudentActions();
  };

  // Handle session selection from Setup modal
  const handleSetupSessionSelect = async (selectedSessionId: number) => {
    if (!selectedStudent) {
      toast.error('Veuillez d\'abord sélectionner un élève');
      return;
    }

    const activeSessionId = studentSessionMap.get(selectedStudent.id);
    if (!activeSessionId) {
      toast.error('Session introuvable pour cet élève');
      return;
    }

    try {
      // Fetch the full session with its items
      const fullSession = await sessionService.getSession(selectedSessionId);

      if (!fullSession.items || fullSession.items.length === 0) {
        toast.info('Cette séance ne contient aucun élément');
        return;
      }

      const insertIndex = timelineItems.length; // Add at the end
      const newItems: ExtendedSessionItem[] = [];

      for (let index = 0; index < fullSession.items.length; index++) {
        const sessionItem = fullSession.items[index];
        const backendItem = await activeSessionService.createItem({
          session: activeSessionId,
          item_type: sessionItem.item_type,
          course: sessionItem.course,
          exercise: sessionItem.exercise,
          video: sessionItem.video,
          order: insertIndex + index,
        });

        newItems.push({
          id: backendItem.id || Date.now() + index,
          item_type: sessionItem.item_type,
          course: sessionItem.course,
          course_title: backendItem.course_title,
          course_thumbnail_url: backendItem.course_thumbnail_url,
          exercise: sessionItem.exercise,
          exercise_pattern: backendItem.exercise_pattern_title,
          exercise_pattern_thumbnail_url: backendItem.exercise_pattern_thumbnail_url,
          video: sessionItem.video,
          video_title: backendItem.video_title,
          video_thumbnail_url: backendItem.video_thumbnail_url,
          order: insertIndex + index,
        });
      }

      setTimelineItems([...timelineItems, ...newItems]);

      // Log action: session added to student's timeline
      try {
        await studentActionService.logSessionAdded(selectedStudent.id, selectedSessionId);
        if (exchangesDialogOpen) {
          loadStudentActions();
        }
      } catch (error) {
        console.error('Error logging session added action:', error);
      }

      toast.success(`Séance ajoutée à la trame (${newItems.length} éléments)`);
    } catch (error) {
      console.error('Error adding session from setup:', error);
      toast.error('Erreur lors de l\'ajout de la séance');
    }
  };

  // Toggle item selection for partial preview
  const handleToggleItemSelection = (itemId: number) => {
    setSelectedItemIds(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Generate partial preview
  const handleGeneratePartialPreview = async () => {
    if (!selectedStudent || selectedItemIds.length === 0) {
      toast.error('Veuillez sélectionner au moins un élément');
      return;
    }

    try {
      setGeneratingPartialPdf(true);
      if (partialPdfUrl) {
        URL.revokeObjectURL(partialPdfUrl);
      }

      // Create a temporary session with selected items
      const selectedItems = timelineItems.filter(item => selectedItemIds.includes(item.id));

      // For each selected item, we need to convert it to the session item format
      const sessionItems = selectedItems.map((item, index) => ({
        id: item.id,
        item_type: item.item_type as 'course' | 'exercise' | 'video',
        course: item.item_type === 'course' ? item.course : null,
        exercise: item.item_type === 'exercise' ? item.exercise : null,
        video: item.item_type === 'video' ? item.video : null,
        order: index,
      }));

      // Create a temporary session
      const tempSession = await sessionService.create({
        name: `Preview temporaire - ${selectedStudent.first_name}`,
        is_public: false,
      });

      // Add items to the session
      for (const item of sessionItems) {
        await sessionService.addItem(tempSession.id, {
          item_type: item.item_type,
          course_id: item.course || undefined,
          exercise_id: item.exercise || undefined,
          video_id: item.video || undefined,
        });
      }

      // Generate preview
      const blobUrl = await sessionService.generatePreview(tempSession.id);
      setPartialPdfUrl(blobUrl);
      setPartialPreviewOpen(true);

      // Delete temporary session
      await sessionService.delete(tempSession.id);

      toast.success('Preview partiel généré avec succès');
    } catch (error) {
      console.error('Error generating partial preview:', error);
      toast.error('Erreur lors de la génération du preview partiel');
    } finally {
      setGeneratingPartialPdf(false);
    }
  };

  // Close partial preview modal
  const handleClosePartialPreview = () => {
    setPartialPreviewOpen(false);
    if (partialPdfUrl) {
      URL.revokeObjectURL(partialPdfUrl);
      setPartialPdfUrl(null);
    }
  };

  // Drag selection handlers
  const handleItemMouseDown = (index: number, e: React.MouseEvent) => {
    setIsDragSelecting(true);
    setDragStartIndex(index);

    // Toggle selection of the clicked item
    const item = timelineItems[index];
    if (item) {
      handleToggleItemSelection(item.id);
    }
  };

  const handleItemMouseEnter = (index: number) => {
    if (isDragSelecting && dragStartIndex !== null) {
      // Select all items between start and end
      const start = Math.min(dragStartIndex, index);
      const end = Math.max(dragStartIndex, index);
      const idsToSelect = timelineItems.slice(start, end + 1).map(item => item.id);

      setSelectedItemIds(idsToSelect);
    }
  };

  const handleMouseUp = () => {
    setIsDragSelecting(false);
    setDragStartIndex(null);
  };

  const handleDeselectAll = () => {
    setSelectedItemIds([]);
  };

  // Global mouse up listener to end drag selection
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragSelecting) {
        handleMouseUp();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragSelecting]);

  // Expand session to view its items
  const handleExpandSession = async (session: Session) => {
    try {
      const fullSession = await sessionService.getSession(session.id);
      setExpandedSession(fullSession);
    } catch (error) {
      console.error('Error fetching session details:', error);
      toast.error('Erreur lors du chargement de la séance');
    }
  };

  // Expand teacher to view their content
  const handleExpandTeacher = async (teacher: User) => {
    try {
      setExpandedTeacher(teacher);
      // Fetch teacher's content
      const [tCourses, tExercises, tVideos, tSessions] = await Promise.all([
        courseService.getCourses(),
        exerciseService.getExercises(),
        videoService.getAll(),
        sessionService.getAll(),
      ]);
      // Filter by teacher
      setTeacherCourses(tCourses.filter(c => c.created_by === teacher.id));
      setTeacherExercises(tExercises.filter(e => e.created_by === teacher.id));
      setTeacherVideos(tVideos.filter(v => v.created_by === teacher.id));
      setTeacherSessions(tSessions.filter(s => s.created_by === teacher.id));
    } catch (error) {
      console.error('Error fetching teacher content:', error);
      toast.error('Erreur lors du chargement du contenu de l\'enseignant');
    }
  };

  // Navigate into a course container
  const handleCourseContainerClick = (container: CourseContainer) => {
    setCourseNavigationPath([...courseNavigationPath, container]);
    setCurrentCourseContainer(container);
  };

  // Navigate back in course containers
  const handleCourseBackClick = () => {
    const newPath = [...courseNavigationPath];
    newPath.pop();
    setCourseNavigationPath(newPath);
    setCurrentCourseContainer(newPath[newPath.length - 1] || null);
  };

  // Navigate into a pattern container
  const handlePatternContainerClick = (container: any) => {
    setPatternNavigationPath([...patternNavigationPath, container]);
    setCurrentPatternContainer(container);
  };

  // Navigate back in pattern containers
  const handlePatternBackClick = () => {
    const newPath = [...patternNavigationPath];
    newPath.pop();
    setPatternNavigationPath(newPath);
    setCurrentPatternContainer(newPath[newPath.length - 1] || null);
  };

  // Navigate into an exercise container
  const handleExerciseContainerClick = (container: ExerciseContainer) => {
    setExerciseNavigationPath([...exerciseNavigationPath, container]);
    setCurrentExerciseContainer(container);
  };

  // Navigate back in exercise containers
  const handleExerciseBackClick = () => {
    const newPath = [...exerciseNavigationPath];
    newPath.pop();
    setExerciseNavigationPath(newPath);
    setCurrentExerciseContainer(newPath[newPath.length - 1] || null);
  };

  // Navigate into a session container
  const handleSessionContainerClick = (container: SessionContainer) => {
    setSessionNavigationPath([...sessionNavigationPath, container]);
    setCurrentSessionContainer(container);
  };

  // Navigate back in session containers
  const handleSessionBackClick = () => {
    const newPath = [...sessionNavigationPath];
    newPath.pop();
    setSessionNavigationPath(newPath);
    setCurrentSessionContainer(newPath[newPath.length - 1] || null);
  };

  // Navigate into a video container
  const handleVideoContainerClick = (container: VideoContainer) => {
    setVideoNavigationPath([...videoNavigationPath, container]);
    setCurrentVideoContainer(container);
  };

  // Navigate back in video containers
  const handleVideoBackClick = () => {
    const newPath = [...videoNavigationPath];
    newPath.pop();
    setVideoNavigationPath(newPath);
    setCurrentVideoContainer(newPath[newPath.length - 1] || null);
  };

  // Filter suggestions to exclude active students
  const filteredSuggestions = suggestedStudents.filter(
    (student) => !activeStudents.find((active) => active.id === student.id)
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleTimelineDragStart}
      onDragEnd={handleTimelineDragEnd}
      onDragOver={handleTimelineDragOver}
    >
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', gap: 2, p: 2 }}>
        {/* Left Column - Students */}
        <Paper
          sx={{
            width: 250,
            display: 'flex',
            flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Active Students Section */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderBottom: '2px solid #e0e0e0',
            minHeight: 0,
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Élèves en cours
              </Typography>
              <Tooltip title="Configurer les places">
                <IconButton size="small" onClick={() => setSeatConfigOpen(true)}>
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Seat Configuration Modal */}
            <Dialog open={seatConfigOpen} onClose={() => setSeatConfigOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Configuration des places</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, mt: 1 }}>
                  {availableSeats.map(seat => (
                    <Chip
                      key={seat}
                      label={seat}
                      onDelete={() => handleRemoveSeat(seat)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                  {availableSeats.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Aucune place configurée
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    type="number"
                    size="small"
                    label="N° place"
                    value={newSeatInput}
                    onChange={(e) => setNewSeatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddSeat(); }}
                    inputProps={{ min: 1 }}
                    sx={{ width: 100 }}
                  />
                  <Button variant="contained" size="small" onClick={handleAddSeat}>
                    Ajouter
                  </Button>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleResetSeats} color="secondary" size="small">
                  Réinitialiser (1-6)
                </Button>
                <Button onClick={() => setSeatConfigOpen(false)} variant="contained" size="small">
                  Fermer
                </Button>
              </DialogActions>
            </Dialog>
            <Autocomplete
              size="small"
              options={Array.isArray(allStudents) ? allStudents : []}
              getOptionLabel={(option) => {
                if (!option) return '';
                const lastName = option.last_name || '';
                return `${option.first_name} ${lastName}`.trim();
              }}
              value={searchValue}
              inputValue={inputValue}
              onChange={(_, newValue) => handleSearchSelect(newValue)}
              onInputChange={(event, newInputValue, reason) => {
                // Don't update input value if the change is from a reset
                if (reason !== 'reset') {
                  setInputValue(newInputValue);
                }
              }}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              blurOnSelect
              clearOnBlur
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Rechercher un élève..."
                  variant="outlined"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: option.color,
                      }}
                    />
                    {option.first_name} {option.last_name || ''}
                  </Box>
                </li>
              )}
            />
          </Box>
          <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {activeStudents.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ p: 2, textAlign: 'center' }}
              >
                Glissez des élèves ici ou recherchez-les
              </Typography>
            ) : (
              activeStudents.map((student) => (
                <ListItem
                  key={student.id}
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: selectedStudent?.id === student.id ? 'action.selected' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  onClick={() => setSelectedStudent(student)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: student.color,
                        mr: 1.5,
                      }}
                    />
                    <ListItemText
                      primary={`${student.first_name} ${student.last_name || ''}`.trim()}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                      sx={{ flex: 1, m: 0 }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveStudent(student.id);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3, mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Place:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }} onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={student.seat_number || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') return;
                          handleUpdateSeatNumber(student, val as number);
                        }}
                        displayEmpty
                        sx={{
                          fontSize: '0.875rem',
                          '& .MuiSelect-select': { py: 0.5, textAlign: 'center' },
                        }}
                      >
                        <MenuItem value="">
                          <em>—</em>
                        </MenuItem>
                        {availableSeats.map(seat => {
                          const occupant = activeStudents.find(s => s.id !== student.id && s.seat_number === seat);
                          return (
                            <MenuItem key={seat} value={seat} sx={occupant ? { color: 'text.disabled' } : {}}>
                              {seat}{occupant ? ` (${occupant.first_name})` : ''}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 3 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {student.arrival_time.format('HH:mm')}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRoundArrivalTimeDown(student);
                      }}
                      title="Arrondir au quart d'heure inférieur"
                      sx={{ ml: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 'bold' }}>-¼</Typography>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRoundArrivalTimeUp(student);
                      }}
                      title="Arrondir au quart d'heure supérieur"
                    >
                      <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 'bold' }}>+¼</Typography>
                    </IconButton>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEndSession(student);
                    }}
                    sx={{ mt: 1, ml: 3 }}
                  >
                    Fin de séance
                  </Button>
                </ListItem>
              ))
            )}
          </List>
        </Box>

        {/* Suggested Students Section */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Suggestions
            </Typography>
          </Box>
          <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {filteredSuggestions.map((student) => (
              <ListItem
                key={student.id}
                draggable
                onDragStart={() => handleDragStart(student)}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  cursor: 'grab',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                secondaryAction={<DragIcon fontSize="small" color="action" />}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: student.color,
                    mr: 1.5,
                  }}
                />
                <ListItemText
                  primary={`${student.first_name} ${student.last_name || ''}`.trim()}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{ p: 1, borderTop: '1px solid #e0e0e0' }}>
          {/* Student Actions Block */}
          {selectedStudent && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Actions pour {selectedStudent.first_name}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ObjectiveIcon />}
                  onClick={() => setObjectivesDialogOpen(true)}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Objectifs
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<QuizIcon />}
                  onClick={() => setQuizzesDialogOpen(true)}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Sessions de quizz
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryDialogOpen(true)}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Historique
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ChatIcon />}
                  onClick={handleOpenExchanges}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Échanges
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SetupIcon />}
                  onClick={() => setSetupDialogOpen(true)}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Setup
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Middle Column - Session Timeline */}
      <Paper
        sx={{
          width: 400,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {selectedStudent ? (
          <>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: selectedStudent.color,
                  }}
                />
                <Typography variant="h6">
                  {`${selectedStudent.first_name} ${selectedStudent.last_name || ''}`.trim()}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="textSecondary">
                  Trame de la séance
                </Typography>
                {selectedItemIds.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={generatingPartialPdf ? <CircularProgress size={16} /> : <PdfIcon />}
                    onClick={handleGeneratePartialPreview}
                    disabled={generatingPartialPdf}
                  >
                    Visualiser ce bout de trame ({selectedItemIds.length})
                  </Button>
                )}
              </Box>
            </Box>
            <DroppableTimelineArea isOver={overId === 'timeline-drop-area'} onClickBackground={handleDeselectAll}>
              {timelineItems.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', mt: 4 }}>
                  Glissez des cours, exercices, vidéos ou séances ici
                </Typography>
              ) : (
                <SortableContext
                  items={timelineItems.map(item => `timeline-item-${item.id}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <List sx={{ p: 0 }}>
                    {timelineItems.map((item, index) => (
                      <SortableTimelineItem
                        key={item.id}
                        item={item}
                        index={index}
                        onRemove={handleRemoveTimelineItem}
                        onOpenQuickRecap={handleOpenQuickRecap}
                        onEvaluateExercise={handleOpenEvaluationModal}
                        isOver={overId === `timeline-item-${item.id}` && activeId !== `timeline-item-${item.id}`}
                        isSelected={selectedItemIds.includes(item.id)}
                        onItemMouseDown={handleItemMouseDown}
                        onItemMouseEnter={handleItemMouseEnter}
                      />
                    ))}
                  </List>
                </SortableContext>
              )}
            </DroppableTimelineArea>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body1" color="textSecondary">
              Sélectionnez un élève pour voir la trame de sa séance
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Suggestions column */}
      <Paper sx={{ width: 400, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <SuggestionsColumn student={selectedStudent} onSessionCreated={() => setTimelineReload((n) => n + 1)} />
      </Paper>

      {/* Right Column - Navigation Tabs */}
      <Paper
        sx={{
          width: 400,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {['Cours', 'Patterns', 'Exercices', 'Séances', 'Vidéos', 'Enseignants', 'Récent'].map((label, idx) => (
                <Button
                  key={label}
                  size="small"
                  onClick={() => setRightTabValue(idx)}
                  sx={{
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1,
                    minWidth: 'auto',
                    borderRadius: 0,
                    color: rightTabValue === idx ? 'primary.main' : 'text.secondary',
                    borderBottom: rightTabValue === idx ? '2px solid' : '2px solid transparent',
                    borderColor: rightTabValue === idx ? 'primary.main' : 'transparent',
                    fontWeight: rightTabValue === idx ? 'bold' : 'normal',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, minHeight: 0 }}>
          {/* Cours Tab */}
          {rightTabValue === 0 && (
            <Box>
              {/* Back button if in a container */}
              {currentCourseContainer && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <IconButton size="small" onClick={handleCourseBackClick}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {currentCourseContainer.title}
                  </Typography>
                </Box>
              )}
              <List sx={{ p: 0 }}>
                {/* Display containers first */}
                {courseContainers.map((container) => (
                  <ListItem
                    key={`container-${container.id}`}
                    onClick={() => handleCourseContainerClick(container)}
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <FolderIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <ListItemText
                      primary={container.title}
                      secondary={`${container.children_count || 0} contenants, ${container.courses_count || 0} cours`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
                {/* Then display courses */}
                {courses.map((course) => (
                  <DraggableNavItem key={`course-${course.id}`} id={`nav-course-${course.id}`}>
                    <ListItem
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                      }}
                      secondaryAction={<DragIcon fontSize="small" color="action" />}
                    >
                      <Avatar
                        src={course.thumbnail_url}
                        variant="rounded"
                        sx={{ width: 40, height: 40, mr: 1.5 }}
                      >
                        <FileIcon />
                      </Avatar>
                      <ListItemText
                        primary={course.title}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      />
                    </ListItem>
                  </DraggableNavItem>
                ))}
                {courseContainers.length === 0 && courses.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                    Aucun cours trouvé
                  </Typography>
                )}
              </List>
            </Box>
          )}

          {/* Patterns Tab */}
          {rightTabValue === 1 && (
            <Box>
              {/* Back button if in a container */}
              {currentPatternContainer && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <IconButton size="small" onClick={handlePatternBackClick}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {currentPatternContainer.title}
                  </Typography>
                </Box>
              )}
              <List sx={{ p: 0 }}>
                {/* Display containers first */}
                {patternContainers.map((container) => (
                  <ListItem
                    key={`container-${container.id}`}
                    onClick={() => handlePatternContainerClick(container)}
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <FolderIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <ListItemText
                      primary={container.title}
                      secondary={`${container.children_count || 0} contenants, ${container.patterns_count || 0} patterns`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
                {/* Composite patterns */}
                {compositePatterns.map((cp) => (
                  <DraggableNavItem key={`composite-${cp.id}`} id={`nav-compositepattern-${cp.id}`}>
                    <ListItem
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(156, 39, 176, 0.04)',
                        border: '2px solid',
                        borderColor: 'secondary.main',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                      }}
                      secondaryAction={<DragIcon fontSize="small" color="secondary" />}
                    >
                      <ListItemText
                        primary={cp.title}
                        secondary={`${cp.child_count} patterns`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption', color: 'secondary' }}
                      />
                    </ListItem>
                  </DraggableNavItem>
                ))}
                {/* Then display patterns */}
                {patterns.map((pattern) => (
                  <DraggableNavItem key={`pattern-${pattern.id}`} id={`nav-pattern-${pattern.id}`}>
                    <ListItem
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                      }}
                      secondaryAction={<DragIcon fontSize="small" color="action" />}
                    >
                      <Avatar
                        src={pattern.thumbnail_url}
                        variant="rounded"
                        sx={{ width: 40, height: 40, mr: 1.5 }}
                      >
                        <FileIcon />
                      </Avatar>
                      <ListItemText
                        primary={pattern.title || `Pattern #${pattern.id}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      />
                    </ListItem>
                  </DraggableNavItem>
                ))}
                {patternContainers.length === 0 && patterns.length === 0 && compositePatterns.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                    Aucun pattern trouvé
                  </Typography>
                )}
              </List>
            </Box>
          )}

          {/* Exercices Tab */}
          {rightTabValue === 2 && (
            <Box>
              {/* Back button if in a container */}
              {currentExerciseContainer && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <IconButton size="small" onClick={handleExerciseBackClick}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {currentExerciseContainer.title}
                  </Typography>
                </Box>
              )}
              <List sx={{ p: 0 }}>
                {/* Display containers first */}
                {exerciseContainers.map((container) => (
                  <ListItem
                    key={`container-${container.id}`}
                    onClick={() => handleExerciseContainerClick(container)}
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <FolderIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <ListItemText
                      primary={container.title}
                      secondary={`${container.children_count || 0} contenants, ${container.exercises_count || 0} exercices`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
                {/* Then display exercises */}
                {exercises.map((exercise) => (
                  <DraggableNavItem key={`exercise-${exercise.id}`} id={`nav-exercise-${exercise.id}`}>
                    <ListItem
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                      }}
                      secondaryAction={<DragIcon fontSize="small" color="action" />}
                    >
                      <Avatar
                        src={exercise.pattern_thumbnail_url}
                        variant="rounded"
                        sx={{ width: 40, height: 40, mr: 1.5 }}
                      >
                        <ExerciseIcon />
                      </Avatar>
                      <ListItemText
                        primary={exercise.pattern_title || `Exercise #${exercise.id}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      />
                    </ListItem>
                  </DraggableNavItem>
                ))}
                {exerciseContainers.length === 0 && exercises.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                    Aucun exercice trouvé
                  </Typography>
                )}
              </List>
            </Box>
          )}

          {/* Séances Tab */}
          {rightTabValue === 3 && (
            <Box>
              {/* Back button for container navigation (only show if not in expanded session) */}
              {currentSessionContainer && !expandedSession && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <IconButton size="small" onClick={handleSessionBackClick}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {currentSessionContainer.title}
                  </Typography>
                </Box>
              )}
              <List sx={{ p: 0 }}>
                {expandedSession ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton size="small" onClick={() => setExpandedSession(null)}>
                      <CloseIcon />
                    </IconButton>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {expandedSession.name}
                    </Typography>
                  </Box>
                  {expandedSession.items.map((item) => {
                    const itemId = item.item_type === 'course' ? item.course :
                                   item.item_type === 'exercise' ? item.exercise :
                                   item.video;
                    return (
                      <DraggableNavItem key={item.id} id={`nav-${item.item_type}-${itemId}`}>
                        <ListItem
                          sx={{
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                          }}
                          secondaryAction={<DragIcon fontSize="small" color="action" />}
                        >
                          <Avatar
                            src={item.item_type === 'video' ? item.video_thumbnail_url : undefined}
                            variant="rounded"
                            sx={{ width: 40, height: 40, mr: 1.5 }}
                          >
                            {item.item_type === 'course' ? <FileIcon /> :
                             item.item_type === 'exercise' ? <ExerciseIcon /> :
                             <VideoIcon />}
                          </Avatar>
                          <ListItemText
                            primary={
                              item.item_type === 'course' ? item.course_title :
                              item.item_type === 'exercise' ? item.exercise_pattern :
                              item.video_title
                            }
                            secondary={
                              <Chip
                                label={item.item_type === 'course' ? 'Cours' :
                                       item.item_type === 'exercise' ? 'Exercice' :
                                       'Vidéo'}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            }
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                          />
                        </ListItem>
                      </DraggableNavItem>
                    );
                  })}
                </>
              ) : (
                <>
                  {/* Display containers first */}
                  {sessionContainers.map((container) => (
                    <ListItem
                      key={`container-${container.id}`}
                      onClick={() => handleSessionContainerClick(container)}
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <FolderIcon sx={{ mr: 1.5, color: 'action.active' }} />
                      <ListItemText
                        primary={container.title}
                        secondary={`${container.children_count || 0} contenants, ${container.sessions_count || 0} séances`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                  {/* Then display sessions */}
                  {sessions.map((session) => (
                    <DraggableNavItem key={session.id} id={`nav-session-${session.id}`}>
                      <ListItem
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'grab',
                          '&:active': { cursor: 'grabbing' },
                        }}
                        secondaryAction={
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExpandSession(session);
                              }}
                            >
                              <Typography variant="caption">...</Typography>
                            </IconButton>
                            <DragIcon fontSize="small" color="action" />
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={session.name}
                          secondary={`${session.items?.length || 0} éléments`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                          sx={{ ml: 4 }}
                        />
                      </ListItem>
                    </DraggableNavItem>
                  ))}
                  {sessionContainers.length === 0 && sessions.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                      Aucune séance trouvée
                    </Typography>
                  )}
                </>
              )}
              </List>
            </Box>
          )}

          {/* Vidéos Tab */}
          {rightTabValue === 4 && (
            <Box>
              {/* Back button if in a container */}
              {currentVideoContainer && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <IconButton size="small" onClick={handleVideoBackClick}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {currentVideoContainer.title}
                  </Typography>
                </Box>
              )}
              <List sx={{ p: 0 }}>
                {/* Display containers first */}
                {videoContainers.map((container) => (
                  <ListItem
                    key={`container-${container.id}`}
                    onClick={() => handleVideoContainerClick(container)}
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <FolderIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <ListItemText
                      primary={container.title}
                      secondary={`${container.children_count || 0} contenants, ${container.videos_count || 0} vidéos`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
                {/* Then display videos */}
                {videos.map((video) => (
                  <DraggableNavItem key={`video-${video.id}`} id={`nav-video-${video.id}`}>
                    <ListItem
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                      }}
                      secondaryAction={<DragIcon fontSize="small" color="action" />}
                    >
                      <Avatar
                        src={video.thumbnail_url || undefined}
                        variant="rounded"
                        sx={{ width: 40, height: 40, mr: 1.5 }}
                      >
                        <VideoIcon />
                      </Avatar>
                      <ListItemText
                        primary={video.title}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      />
                    </ListItem>
                  </DraggableNavItem>
                ))}
                {videoContainers.length === 0 && videos.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                    Aucune vidéo trouvée
                  </Typography>
                )}
              </List>
            </Box>
          )}

          {/* Enseignants Tab */}
          {rightTabValue === 5 && (
            <Box>
              {expandedTeacher ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton size="small" onClick={() => setExpandedTeacher(null)}>
                      <CloseIcon />
                    </IconButton>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {expandedTeacher.first_name} {expandedTeacher.last_name}
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Cours ({teacherCourses.length})
                  </Typography>
                  <List sx={{ p: 0, mb: 2 }}>
                    {teacherCourses.map((course) => (
                      <DraggableNavItem key={course.id} id={`nav-course-${course.id}`}>
                        <ListItem
                          sx={{
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                          }}
                          secondaryAction={<DragIcon fontSize="small" color="action" />}
                        >
                          <ListItemText
                            primary={course.title}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </DraggableNavItem>
                    ))}
                  </List>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Exercices ({teacherExercises.length})
                  </Typography>
                  <List sx={{ p: 0, mb: 2 }}>
                    {teacherExercises.map((exercise) => (
                      <DraggableNavItem key={exercise.id} id={`nav-exercise-${exercise.id}`}>
                        <ListItem
                          sx={{
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                          }}
                          secondaryAction={<DragIcon fontSize="small" color="action" />}
                        >
                          <ListItemText
                            primary={exercise.pattern_title || `Exercise #${exercise.id}`}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </DraggableNavItem>
                    ))}
                  </List>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Vidéos ({teacherVideos.length})
                  </Typography>
                  <List sx={{ p: 0, mb: 2 }}>
                    {teacherVideos.map((video) => (
                      <DraggableNavItem key={video.id} id={`nav-video-${video.id}`}>
                        <ListItem
                          sx={{
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                          }}
                          secondaryAction={<DragIcon fontSize="small" color="action" />}
                        >
                          <ListItemText
                            primary={video.title}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </DraggableNavItem>
                    ))}
                  </List>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Séances ({teacherSessions.length})
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {teacherSessions.map((session) => (
                      <DraggableNavItem key={session.id} id={`nav-session-${session.id}`}>
                        <ListItem
                          sx={{
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                          }}
                          secondaryAction={<DragIcon fontSize="small" color="action" />}
                        >
                          <ListItemText
                            primary={session.name}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </DraggableNavItem>
                    ))}
                  </List>
                </>
              ) : (
                <List sx={{ p: 0 }}>
                  {teachers.map((teacher) => (
                    <ListItem
                      key={teacher.id}
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={() => handleExpandTeacher(teacher)}
                    >
                      <ListItemText
                        primary={`${teacher.first_name} ${teacher.last_name}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      />
                    </ListItem>
                  ))}
                  {teachers.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                      Aucun enseignant trouvé
                    </Typography>
                  )}
                </List>
              )}
            </Box>
          )}

          {/* Récemment intégré Tab */}
          {rightTabValue === 6 && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Éléments récemment intégrés
              </Typography>
              <List sx={{ p: 0 }}>
                {recentlyIntegrated.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                    Aucun élément récemment intégré
                  </Typography>
                ) : (
                  recentlyIntegrated.map((item) => (
                    <DraggableNavItem key={item.id} id={`nav-${item.type}-${item.itemId}`}>
                      <ListItem
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'grab',
                          '&:active': { cursor: 'grabbing' },
                        }}
                        secondaryAction={<DragIcon fontSize="small" color="action" />}
                      >
                        <Avatar
                          src={item.thumbnail_url}
                          variant="rounded"
                          sx={{ width: 40, height: 40, mr: 1.5 }}
                        >
                          {item.type === 'course' ? <FileIcon /> :
                           item.type === 'exercise' ? <ExerciseIcon /> :
                           <VideoIcon />}
                        </Avatar>
                        <ListItemText
                          primary={item.title}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Chip
                                label={item.type === 'course' ? 'Cours' :
                                       item.type === 'exercise' ? 'Exercice' :
                                       'Vidéo'}
                                size="small"
                              />
                              <Typography variant="caption" color="text.secondary">
                                {new Date(item.addedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Box>
                          }
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                        />
                      </ListItem>
                    </DraggableNavItem>
                  ))
                )}
              </List>
            </Box>
          )}
        </Box>
      </Paper>
      {/* End Session Modal */}
      <Dialog
        open={endSessionDialogOpen}
        onClose={() => setEndSessionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Fin de séance - {studentToEnd ? `${studentToEnd.first_name} ${studentToEnd.last_name || ''}`.trim() : ''}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {/* Start Time */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MobileTimePicker
                    label="Heure d'arrivée"
                    value={studentToEnd?.arrival_time || null}
                    onChange={handleStartTimeChange}
                    open={startTimePickerOpen}
                    onOpen={() => setStartTimePickerOpen(true)}
                    onClose={() => setStartTimePickerOpen(false)}
                    ampm={false}
                    minutesStep={5}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        onClick: () => setStartTimePickerOpen(true),
                      },
                      actionBar: {
                        actions: ['accept']
                      }
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRoundStartTimeDown}
                    title="Arrondir au quart d'heure inférieur"
                  >
                    -¼
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRoundStartTimeUp}
                    title="Arrondir au quart d'heure supérieur"
                  >
                    +¼
                  </Button>
                </Box>
              </Box>

              {/* End Time */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MobileTimePicker
                    label="Heure de départ"
                    value={endTime}
                    onChange={handleEndTimeChange}
                    open={endTimePickerOpen}
                    onOpen={() => setEndTimePickerOpen(true)}
                    onClose={() => setEndTimePickerOpen(false)}
                    ampm={false}
                    minutesStep={5}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        onClick: () => setEndTimePickerOpen(true),
                      },
                      actionBar: {
                        actions: ['accept']
                      }
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRoundEndTimeDown}
                    title="Arrondir au quart d'heure inférieur"
                  >
                    -¼
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRoundEndTimeUp}
                    title="Arrondir au quart d'heure supérieur"
                  >
                    +¼
                  </Button>
                </Box>
              </Box>

              {/* Price */}
              <Box>
                <TextField
                  label="Prix du cours"
                  value={sessionPrice}
                  onChange={(e) => setSessionPrice(e.target.value)}
                  size="small"
                  type="number"
                  fullWidth
                  InputProps={{
                    endAdornment: <Typography variant="caption" sx={{ ml: 1 }}>XPF</Typography>
                  }}
                  helperText="Calculé automatiquement à 3000 XPF/heure"
                />

                {/* Price Warning and Auto-Update Button */}
                {studentToEnd?.arrival_time && endTime && (() => {
                  // Normalize times to remove seconds and milliseconds for accurate calculation
                  const startTime = studentToEnd.arrival_time.startOf('minute');
                  const endTimeNormalized = endTime.startOf('minute');

                  const durationInMinutes = endTimeNormalized.diff(startTime, 'minute');
                  const durationInHours = durationInMinutes / 60;
                  const expectedPrice = Math.round(durationInHours * 3000);
                  const currentPrice = parseFloat(sessionPrice || '0');
                  const isPriceIncorrect = currentPrice !== expectedPrice;

                  return isPriceIncorrect ? (
                    <Box sx={{ mt: 1 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'error.main',
                          color: 'white',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          ⚠️ Le prix ne correspond pas à la durée du cours
                        </Typography>
                        <Typography variant="caption">
                          Durée: {durationInHours.toFixed(2)}h × 3000 XPF/h = {expectedPrice} XPF
                        </Typography>
                      </Box>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => setSessionPrice(expectedPrice.toString())}
                      >
                        Ajuster au prix horaire ({expectedPrice} XPF)
                      </Button>
                    </Box>
                  ) : null;
                })()}
              </Box>

              {/* Discount */}
              <TextField
                label="Réduction"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                size="small"
                type="number"
                InputProps={{
                  endAdornment: <Typography variant="caption" sx={{ ml: 1 }}>XPF</Typography>
                }}
              />

              {/* Discount Label */}
              <TextField
                label="Label de réduction"
                value={discountLabel}
                onChange={(e) => setDiscountLabel(e.target.value)}
                size="small"
                placeholder="Raison de la réduction (optionnel)"
              />

              {/* Final Price Display */}
              {discount && (
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Prix initial: {sessionPrice} XPF
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Réduction: -{discount} XPF
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                    Total: {parseFloat(sessionPrice || '0') - parseFloat(discount || '0')} XPF
                  </Typography>
                </Box>
              )}
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="warning"
            onClick={() => handleCreateInvoice('draft')}
          >
            Factures avant émission
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={() => handleCreateInvoice('issued')}
          >
            Factures émises
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setEndSessionDialogOpen(false)}
          >
            Annuler
          </Button>
        </DialogActions>
      </Dialog>

      {/* Objectives Modal */}
      <Dialog
        open={objectivesDialogOpen}
        onClose={() => setObjectivesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Objectifs de {selectedStudent ? `${selectedStudent.first_name} ${selectedStudent.last_name || ''}`.trim() : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Gérez les objectifs assignés à cet élève. Vous pouvez ajouter ou retirer des objectifs.
            </Typography>
            {/* Placeholder for objectives list and management */}
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
              <ObjectiveIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                La gestion des objectifs sera implémentée prochainement.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Cette fonctionnalité reprendra l'interface de l'onglet Objectifs de la page Élève.
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setObjectivesDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quizzes Modal */}
      <Dialog
        open={quizzesDialogOpen}
        onClose={() => setQuizzesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Sessions de quizz - {selectedStudent ? `${selectedStudent.first_name} ${selectedStudent.last_name || ''}`.trim() : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Visualisez l'historique des sessions de quizz et configurez la prochaine session.
            </Typography>
            {/* Placeholder for quiz sessions list and configuration */}
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
              <QuizIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                La gestion des sessions de quizz sera implémentée prochainement.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Cette fonctionnalité reprendra l'interface de l'onglet Quizz de la page Élève.
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuizzesDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Modal */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Historique des activités - {selectedStudent ? `${selectedStudent.first_name} ${selectedStudent.last_name || ''}`.trim() : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Consultez l'historique complet des activités de cet élève.
            </Typography>
            {/* Placeholder for activity history */}
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
              <HistoryIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                L'historique des activités sera implémenté prochainement.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Cette fonctionnalité reprendra l'interface de l'onglet Historique de la page Élève.
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exchanges Modal */}
      <Dialog
        open={exchangesDialogOpen}
        onClose={() => setExchangesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Échanges avec {selectedStudent ? `${selectedStudent.first_name} ${selectedStudent.last_name || ''}`.trim() : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Historique des actions
              </Typography>
              <Button
                size="small"
                onClick={loadStudentActions}
                disabled={loadingActions}
                startIcon={loadingActions ? <CircularProgress size={16} /> : null}
              >
                Actualiser
              </Button>
            </Box>

            {loadingActions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : studentActions.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
                <ChatIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Aucune action enregistrée pour cet élève
                </Typography>
              </Paper>
            ) : (
              <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                {studentActions.map((action, index) => (
                  <React.Fragment key={action.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body1" fontWeight="medium" component="span">
                            {action.action_type_display}
                          </Typography>
                          {action.session_name && (
                            <Chip label={action.session_name} size="small" variant="outlined" />
                          )}
                        </Box>
                        {action.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }} component="div">
                            {action.description}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" component="div">
                          {dayjs(action.created_at).format('DD/MM/YYYY [à] HH:mm')} • Par {action.created_by_name}
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < studentActions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExchangesDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drag Overlay */}
      <DragOverlay dropAnimation={null}>
        {activeDragItem ? (
          <Paper
            sx={{
              p: 1.5,
              bgcolor: 'background.paper',
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              boxShadow: 4,
              cursor: 'grabbing',
              opacity: 0.95,
              maxWidth: 350,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {activeDragItem.type === 'timeline' ? (
                <>
                  <Avatar
                    src={
                      activeDragItem.data.item_type === 'course' ? activeDragItem.data.course_thumbnail_url :
                      activeDragItem.data.item_type === 'exercise' ? activeDragItem.data.exercise_pattern_thumbnail_url :
                      activeDragItem.data.item_type === 'video' ? activeDragItem.data.video_thumbnail_url :
                      undefined
                    }
                    variant="rounded"
                    sx={{ width: 40, height: 40 }}
                  >
                    {activeDragItem.data.item_type === 'course' ? <FileIcon /> :
                     activeDragItem.data.item_type === 'exercise' ? <ExerciseIcon /> :
                     <VideoIcon />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {activeDragItem.data.item_type === 'course' ? activeDragItem.data.course_title :
                       activeDragItem.data.item_type === 'exercise' ? activeDragItem.data.exercise_pattern :
                       activeDragItem.data.item_type === 'video' ? activeDragItem.data.video_title :
                       'Unknown'}
                    </Typography>
                    <Chip
                      label={activeDragItem.data.item_type === 'course' ? 'Cours' :
                             activeDragItem.data.item_type === 'exercise' ? 'Exercice' :
                             'Vidéo'}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <DragIcon sx={{ color: 'action.active' }} />
                </>
              ) : activeDragItem.type === 'session' ? (
                <>
                  <FolderIcon sx={{ fontSize: 40, color: 'action.active' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {activeDragItem.data.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {activeDragItem.data.items?.length || 0} éléments
                    </Typography>
                  </Box>
                  <DragIcon sx={{ color: 'action.active' }} />
                </>
              ) : (
                <>
                  <Avatar
                    src={
                      activeDragItem.type === 'course' ? activeDragItem.data.thumbnail_url :
                      activeDragItem.type === 'exercise' ? activeDragItem.data.pattern_thumbnail_url :
                      activeDragItem.type === 'video' ? activeDragItem.data.thumbnail_url :
                      undefined
                    }
                    variant="rounded"
                    sx={{ width: 40, height: 40 }}
                  >
                    {activeDragItem.type === 'course' ? <FileIcon /> :
                     activeDragItem.type === 'exercise' ? <ExerciseIcon /> :
                     <VideoIcon />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {activeDragItem.type === 'course' ? activeDragItem.data.title :
                       activeDragItem.type === 'exercise' ? activeDragItem.data.pattern_title :
                       activeDragItem.data.title}
                    </Typography>
                    <Chip
                      label={activeDragItem.type === 'course' ? 'Cours' :
                             activeDragItem.type === 'exercise' ? 'Exercice' :
                             'Vidéo'}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <DragIcon sx={{ color: 'action.active' }} />
                </>
              )}
            </Box>
          </Paper>
        ) : null}
      </DragOverlay>

      {/* Quick Recap Modal */}
      <Dialog
        open={quickRecapDialogOpen}
        onClose={() => setQuickRecapDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SuggestionIcon color="info" />
              <Typography variant="h6">Quick Recap</Typography>
            </Box>
            <IconButton
              edge="end"
              onClick={() => setQuickRecapDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {quickRecapItem && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Références pour :{' '}
                <strong>
                  {quickRecapItem.item_type === 'course'
                    ? quickRecapItem.course_title
                    : quickRecapItem.item_type === 'video'
                    ? quickRecapItem.video_title
                    : quickRecapItem.exercise_pattern}
                </strong>
              </Typography>

              {quickRecapItem.quick_recap_course && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FileIcon color="primary" />
                    <Typography variant="h6">Cours de référence</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                    {quickRecapItem.quick_recap_course_title}
                  </Typography>
                  {quickRecapItem.quick_recap_course_pdf_url ? (
                    <PdfDisplay
                      url={quickRecapItem.quick_recap_course_pdf_url}
                      title={quickRecapItem.quick_recap_course_title || 'Cours de référence'}
                    />
                  ) : (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      PDF non disponible
                    </Typography>
                  )}
                </Paper>
              )}

              {quickRecapItem.quick_recap_video && (
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <VideoIcon color="primary" />
                    <Typography variant="h6">Vidéo de référence</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                    {quickRecapItem.quick_recap_video_title}
                  </Typography>
                  {quickRecapItem.quick_recap_video_url ? (
                    <Box
                      sx={{
                        width: '100%',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <video
                        controls
                        style={{
                          width: '100%',
                          maxHeight: '500px',
                          backgroundColor: '#000',
                        }}
                      >
                        <source src={quickRecapItem.quick_recap_video_url} type="video/mp4" />
                        Votre navigateur ne supporte pas la lecture de vidéos.
                      </video>
                    </Box>
                  ) : (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Vidéo non disponible
                    </Typography>
                  )}
                </Paper>
              )}

              {!quickRecapItem.quick_recap_course && !quickRecapItem.quick_recap_video && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  Aucune référence disponible
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickRecapDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Partial Preview Modal */}
      <Dialog
        open={partialPreviewOpen}
        onClose={handleClosePartialPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Preview partiel de la trame ({selectedItemIds.length} élément{selectedItemIds.length > 1 ? 's' : ''})
        </DialogTitle>
        <DialogContent>
          {partialPdfUrl && (
            <Box sx={{ width: '100%', height: '70vh' }}>
              <iframe
                ref={partialPdfIframeRef}
                src={partialPdfUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="Partial Timeline Preview"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              // Print directly from iframe
              if (partialPdfIframeRef.current?.contentWindow) {
                partialPdfIframeRef.current.contentWindow.print();
              }
            }}
            startIcon={<PrintIcon />}
            variant="contained"
          >
            Imprimer
          </Button>
          <Button onClick={handleClosePartialPreview}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pattern Instance Modal */}
      <PatternInstanceModal
        open={patternInstanceModalOpen}
        onClose={() => {
          setPatternInstanceModalOpen(false);
          setSelectedPatternForInstances(null);
          setPendingPatternDropIndex(null);
        }}
        onConfirm={handlePatternInstanceConfirm}
        patternTitle={selectedPatternForInstances?.title || ''}
      />

      {/* Composite Pattern Instance Modal */}
      <CompositePatternInstanceModal
        open={compositeModalOpen}
        onClose={() => {
          setCompositeModalOpen(false);
          setSelectedCompositePattern(null);
          setPendingPatternDropIndex(null);
        }}
        onConfirm={handleCompositePatternConfirm}
        compositeId={selectedCompositePattern?.id || 0}
        compositeTitle={selectedCompositePattern?.title || ''}
        items={selectedCompositePattern?.items || []}
      />

      {/* Exercise Evaluation Modal */}
      <ExerciseEvaluationModal
        open={evaluationModalOpen}
        onClose={() => {
          setEvaluationModalOpen(false);
          setExerciseToEvaluate(null);
          setExercisePatternId(undefined);
        }}
        onSave={handleSaveEvaluation}
        exerciseId={exerciseToEvaluate?.exercise || 0}
        patternId={exercisePatternId}
        patternTitle={exerciseToEvaluate?.exercise_pattern || ''}
      />

      {/* Setup Navigation Modal */}
      <SetupNavigationModal
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        onSessionSelect={handleSetupSessionSelect}
      />

      </Box>
    </DndContext>
  );
};

export default InProgress;
